<template>
    <div v-if="direct_shell || views.length" class="tt-window-manager">
        <!-- Transient JavaScript shells share this dialog owner without entering
             the authenticated view stack. Teleport keeps foreground ordering above
             the app, while omitting routed-window markers excludes layout/help APIs. -->
        <Teleport v-if="direct_shell" to="body">
            <div class="direct-shell-overlay tt-component" :data-tt-overlay="direct_shell.name">
                <!-- Direct authentication shells bypass the routed window stack. Their content
                     emits the same readiness event while this manager retains the dialog boundary. -->
                <section :id="direct_shell.name" ref="direct_dialog" class="direct-shell-dialog" :style="{ '--direct-shell-width': normalizeEditShellWidth( direct_shell.width ) || '32rem', '--direct-shell-height': normalizeEditShellHeight( direct_shell.height ) || 'auto' }" role="dialog" aria-modal="true" :aria-label="direct_shell.view_label" :aria-busy="String( direct_processing )" tabindex="-1" :data-tt-modal="direct_shell.name" :data-tt-screen="direct_shell.view_id || null" @keydown="onWindowKeydown( $event )" @tt-window-first-paint-ready="onDirectWindowFirstPaintReady">
                    <component :is="direct_component" ref="direct_content" v-bind="direct_props" @update:processing="direct_processing = $event === true"/>
                </section>
            </div>
        </Teleport>
        <!-- Routed Wizards and shells remove the dialog fallback from sequential focus;
             the manager focuses it only when no enabled owner control is available. -->
        <!-- Record editors choose source-pane or workspace bounds independently of modality.
             This companion class retains the established geometry for other modal types;
             companion ancestry still owns interaction and ARIA exclusivity in either mode. -->
        <div
            v-else
            ref="layout_grid"
            class="layout-window-grid"
            :class="{ 'has-active-modal-side-panel': has_active_modal_side_panel }"
            :style="gridStyle"
            @click="closeMenus"
            @keydown.esc="onGridEscape"
            @dragover.capture="onGridDragOver"
            @drop.capture="onGridDrop"
            @dragleave.capture="onGridDragLeave"
        >
            <div v-if="menu.active_id" class="window-menu-backdrop" @click="closeMenus"></div>
            <!-- `aria-modal` is intentionally conditional below. A Formula Builder plus Assistant
                 pane is one composite working surface, so claiming an exclusive modal would tell
                 assistive technology that the still-interactive pane is unavailable.
                 data-tt-title is the readable record title; data-tt-screen retains
                 its stable machine identity across record and locale changes.
                 Issue #3752: the same window boundary listens for a bubbling owner
                 first-paint event; individual routed components stay responsible for
                 their content certificate, while this manager owns focus recovery. -->
            <div
                v-for="(window_data, index) in grid_windows"
                :key="window_data.window.id"
                v-show="isWindowRendered( window_data.window )"
                class="layout-window"
                :class="windowClasses(window_data.window.id)"
                :style="windowStyleVars(window_data, index)"
                :role="getWindowRole( window_data.window )"
                :aria-label="getViewLabel( window_data.window )"
                :aria-modal="isWindowExclusiveActiveModal( window_data.window ) ? 'true' : null"
                :aria-busy="isTTSShellWindow( window_data.window.id ) ? String( isWindowCloseBlocked( window_data.window.id ) ) : null"
                :tabindex="isManagedFocusWindow( window_data.window.id ) ? -1 : null"
                :aria-hidden="String( isWindowAutomationHidden( window_data.window ) )"
                :inert="isWindowAutomationHidden( window_data.window ) ? '' : null"
                :data-tt-active-view="String( isWindowAutomationActive( window_data.window ) )"
                :data-tt-view-id="window_data.window.id"
                :data-tt-module="getViewModule( window_data.window )"
                :data-tt-action="getViewAction( window_data.window )"
                :data-tt-screen="getViewScreen( window_data.window )"
                :data-tt-title="getViewLabel( window_data.window )"
                @mousedown.capture="setActiveWindow(window_data.window.id)"
                @keydown="onWindowKeydown( $event, window_data.window )"
                @tt-edit-lane-count="onEditLaneCount( window_data.window.id, $event )"
                @dragover="onWindowDragOver( $event, window_data.window.id )"
                @dragleave="onWindowDragLeave( $event, window_data.window.id )"
                @drop="onWindowDrop( $event, window_data.window.id )"
                @tt-window-first-paint-ready="onWindowFirstPaintReady( window_data.window.id )"
            >
                <div class="window-shell" :class="{ 'modal-window-shell': isModalWindow( window_data.window.id ) }">
                    <!-- Wizard Close remains workflow-owned; other modals use the shared Windows-style caption order. -->
                    <div
                        v-if="isModalWindow( window_data.window.id ) && !isWizardWindow( window_data.window.id )"
                        class="window-menu window-caption-controls"
                        role="group"
                        :aria-label="getWindowControlsLabel( window_data.window )"
                        data-tt-widget="window-controls"
                        :data-tt-view-id="window_data.window.id"
                        @click.stop
                    >
                        <div
                            class="window-caption-controls__buttons"
                        >
                            <!-- TTInteractionManager's capture-phase listener records this
                                 stable action/view identity before Vue runs the handler.
                                 Do not restore `$event` forwarding here: native events retain
                                 the shell DOM and force every Wizard/window caller to learn a
                                 presentation-only coordinate contract. -->
                            <TTWindowCaptionButton
                                kind="minimize"
                                :disabled="!canMinimizeWindow( window_data.window )"
                                :title="getWindowActionLabel( labels.minimize_view, window_data.window )"
                                :aria-label="getWindowActionLabel( labels.minimize_view, window_data.window )"
                                :aria-disabled="String( !canMinimizeWindow( window_data.window ) )"
                                data-tt-action="window-minimize"
                                :data-tt-action-label="labels.minimize_view"
                                :data-tt-view-id="window_data.window.id"
                                @click.stop="minimizeWindow( window_data.window.id )"
                            />
                            <TTWindowCaptionButton
                                kind="menu"
                                icon="tticon tticon-pop_out"
                                :title="labels.window_tooltip"
                                :aria-label="getWindowMenuLabel( window_data.window )"
                                aria-haspopup="menu"
                                :aria-expanded="String( menu.active_id === window_data.window.id )"
                                :aria-controls="getWindowMenuId( window_data.window.id )"
                                data-tt-action="window-menu"
                                :data-tt-action-label="labels.window_tooltip"
                                :data-tt-view-id="window_data.window.id"
                                draggable="true"
                                @dragstart="onWindowDragStart($event, window_data.window.id)"
                                @dragend="onWindowDragEnd"
                                @click.stop="toggleWindowMenu(window_data.window.id)"
                            />
                            <TTWindowCaptionButton
                                v-if="!isWizardWindow( window_data.window.id )"
                                kind="close"
                                icon="tticon tticon-close_black_24dp"
                                :title="getWindowActionLabel( labels.close_view, window_data.window )"
                                :aria-label="getWindowActionLabel( labels.close_view, window_data.window )"
                                :aria-disabled="String( isWindowCloseBlocked( window_data.window.id ) )"
                                :disabled="isWindowCloseBlocked( window_data.window.id )"
                                data-tt-action="window-close"
                                :data-tt-action-label="labels.close_view"
                                :data-tt-view-id="window_data.window.id"
                                @click.stop="closeWindow( window_data.window.id )"
                            />
                        </div>
                    </div>
                    <div
                        v-if="isModalWindow( window_data.window.id ) && menu.active_id === window_data.window.id"
                        :id="getWindowMenuId( window_data.window.id )"
                        class="window-menu-dropdown"
                        role="menu"
                        data-tt-overlay="window-menu"
                        :data-tt-view-id="window_data.window.id"
                        :aria-label="getWindowMenuLabel( window_data.window )"
                        @click.stop
                    >
                        <template v-for="item in getWindowMenuItems(window_data)" :key="item.key">
                            <div v-if="item.type === 'divider'" class="window-menu-divider"></div>
                            <template v-else-if="item.type === 'submenu'">
                                <button
                                    type="button"
                                    class="window-menu-item"
                                    :class="item.class"
                                    :disabled="item.disabled"
                                    role="menuitem"
                                    :aria-expanded="String( item.show === true )"
                                    :data-tt-action="item.data_action"
                                    :data-tt-action-label="item.label"
                                    :data-tt-view-id="window_data.window.id"
                                    @click.stop="item.onClick && item.onClick()"
                                >
                                    <span class="tticon" :class="item.icon" aria-hidden="true"></span>
                                    <span>{{ item.label }}</span>
                                </button>
                                <div v-if="item.show && item.children && item.children.length" class="window-menu-sublist">
                                    <button
                                        v-for="child in item.children"
                                        :key="child.key"
                                        type="button"
                                        class="window-menu-item window-menu-item-sub"
                                        role="menuitem"
                                        :data-tt-action="child.data_action"
                                        :data-tt-action-label="child.label"
                                        :data-tt-view-id="child.view_id"
                                        @click.stop="child.onClick && child.onClick()"
                                    >
                                        <span class="tticon" :class="child.icon" aria-hidden="true"></span>
                                        <span>{{ child.label }}</span>
                                    </button>
                                </div>
                            </template>
                            <button
                                v-else
                                type="button"
                                class="window-menu-item"
                                :class="item.class"
                                :disabled="item.disabled"
                                :title="item.title"
                                :role="item.checked !== undefined ? 'menuitemcheckbox' : 'menuitem'"
                                :aria-checked="item.checked !== undefined ? String( item.checked ) : null"
                                :data-tt-action="item.data_action"
                                :data-tt-action-label="item.label"
                                :data-tt-view-id="window_data.window.id"
                                @click.stop="item.onClick && item.onClick()"
                            >
                                <span v-if="item.checked !== undefined" class="window-menu-checkbox" :class="{ checked: item.checked }" aria-hidden="true"></span>
                                <span v-else class="tticon" :class="item.icon" aria-hidden="true"></span>
                                <span>{{ item.label }}</span>
                            </button>
                        </template>
                    </div>
                    <div class="stacked-view">
                        <router-view :name="window_data.window.name">
                            <component
                                :is="window_data.window.component"
                                :key="getWindowRenderKey( window_data.window )"
                                :tt_route_data="window_data.window.tt_route_data"
                                :route_id="window_data.window.id"
                            />
                        </router-view>
                    </div>
                </div>
                <!-- Issue #3768: the routed first-paint gate keeps incomplete chrome hidden;
                     the modal boundary and Escape handler remain active without a floating status card. -->
            </div>
            <div
                v-for="(window_data, index) in grid_windows"
                :key="window_data.window.id + '-controls'"
                v-show="isWindowRendered( window_data.window ) && !isModalWindow( window_data.window.id ) && !isWindowCoveredByActiveModal( window_data.window ) && hasWindowManagerControls( window_data.window )"
                class="layout-window-controls"
                :class="windowControlClasses( window_data.window.id )"
                :style="windowStyleVars(window_data, index)"
                :data-tt-view-id="window_data.window.id"
            >
                <!-- Route-less shell surfaces own their chrome. Routed pinned panels
                     intentionally omit Minimize but retain Controls and guarded Close. -->
                <div
                    v-if="
                        isWindowRendered( window_data.window )
                            && !isModalWindow( window_data.window.id )
                            && !isWindowCoveredByActiveModal( window_data.window )
                            && hasWindowManagerControls( window_data.window )
                            && !isWizardWindow( window_data.window.id )
                    "
                    class="window-menu window-caption-controls"
                    role="group"
                    :aria-label="getWindowControlsLabel( window_data.window )"
                    data-tt-widget="window-controls"
                    :data-tt-view-id="window_data.window.id"
                    @click.stop
                >
                    <div
                        class="window-caption-controls__buttons"
                    >
                        <!-- Keep the alternate shell on the exact same semantic contract.
                             Its data-tt view/action hooks are consumed by diagnostics,
                             Playwright/Selenium, and minimize-origin correlation; measuring
                             this shell after collapse is too late because it is hidden. -->
                        <TTWindowCaptionButton
                            v-if="!isSidePanelWindow( window_data.window )"
                            kind="minimize"
                            :disabled="!canMinimizeWindow( window_data.window )"
                            :title="getWindowActionLabel( labels.minimize_view, window_data.window )"
                            :aria-label="getWindowActionLabel( labels.minimize_view, window_data.window )"
                            :aria-disabled="String( !canMinimizeWindow( window_data.window ) )"
                            data-tt-action="window-minimize"
                            :data-tt-action-label="labels.minimize_view"
                            :data-tt-view-id="window_data.window.id"
                            @click.stop="minimizeWindow( window_data.window.id )"
                        />
                        <TTWindowCaptionButton
                            kind="menu"
                            icon="tticon tticon-pop_out"
                            :title="labels.window_tooltip"
                            :aria-label="getWindowMenuLabel( window_data.window )"
                            aria-haspopup="menu"
                            :aria-expanded="String( menu.active_id === window_data.window.id )"
                            :aria-controls="getWindowMenuId( window_data.window.id )"
                            data-tt-action="window-menu"
                            :data-tt-action-label="labels.window_tooltip"
                            :data-tt-view-id="window_data.window.id"
                            draggable="true"
                            @mousedown.stop="setActiveWindow( window_data.window.id )"
                            @dragstart="onWindowDragStart($event, window_data.window.id)"
                            @dragend="onWindowDragEnd"
                            @click.stop="toggleWindowMenu(window_data.window.id)"
                        />
                        <TTWindowCaptionButton
                            v-if="!isWizardWindow( window_data.window.id )"
                            kind="close"
                            icon="tticon tticon-close_black_24dp"
                            :title="getWindowActionLabel( labels.close_view, window_data.window )"
                            :aria-label="getWindowActionLabel( labels.close_view, window_data.window )"
                            :aria-disabled="String( isWindowCloseBlocked( window_data.window.id ) )"
                            :disabled="isWindowCloseBlocked( window_data.window.id )"
                            data-tt-action="window-close"
                            :data-tt-action-label="labels.close_view"
                            :data-tt-view-id="window_data.window.id"
                            @click.stop="closeWindow( window_data.window.id )"
                        />
                    </div>
                </div>
                <div
                    v-if="menu.active_id === window_data.window.id && hasWindowManagerControls( window_data.window )"
                    :id="getWindowMenuId( window_data.window.id )"
                    class="window-menu-dropdown"
                    role="menu"
                    data-tt-overlay="window-menu"
                    :data-tt-view-id="window_data.window.id"
                    :aria-label="getWindowMenuLabel( window_data.window )"
                    @click.stop
                >
                    <template v-for="item in getWindowMenuItems(window_data)" :key="item.key">
                        <div v-if="item.type === 'divider'" class="window-menu-divider"></div>
                        <template v-else-if="item.type === 'submenu'">
                            <button
                                type="button"
                                class="window-menu-item"
                                :class="item.class"
                                :disabled="item.disabled"
                                role="menuitem"
                                :aria-expanded="String( item.show === true )"
                                :data-tt-action="item.data_action"
                                :data-tt-action-label="item.label"
                                :data-tt-view-id="window_data.window.id"
                                @click.stop="item.onClick && item.onClick()"
                            >
                                <span class="tticon" :class="item.icon" aria-hidden="true"></span>
                                <span>{{ item.label }}</span>
                            </button>
                            <div v-if="item.show && item.children && item.children.length" class="window-menu-sublist">
                                <button
                                    v-for="child in item.children"
                                    :key="child.key"
                                    type="button"
                                    class="window-menu-item window-menu-item-sub"
                                    role="menuitem"
                                    :data-tt-action="child.data_action"
                                    :data-tt-action-label="child.label"
                                    :data-tt-view-id="child.view_id"
                                    @click.stop="child.onClick && child.onClick()"
                                >
                                    <span class="tticon" :class="child.icon" aria-hidden="true"></span>
                                    <span>{{ child.label }}</span>
                                </button>
                            </div>
                        </template>
                        <button
                            v-else
                            type="button"
                            class="window-menu-item"
                            :class="item.class"
                            :disabled="item.disabled"
                            :title="item.title"
                            :role="item.checked !== undefined ? 'menuitemcheckbox' : 'menuitem'"
                            :aria-checked="item.checked !== undefined ? String( item.checked ) : null"
                            :data-tt-action="item.data_action"
                            :data-tt-action-label="item.label"
                            :data-tt-view-id="window_data.window.id"
                            @click.stop="item.onClick && item.onClick()"
                        >
                            <span v-if="item.checked !== undefined" class="window-menu-checkbox" :class="{ checked: item.checked }" aria-hidden="true"></span>
                            <span v-else class="tticon" :class="item.icon" aria-hidden="true"></span>
                            <span>{{ item.label }}</span>
                        </button>
                    </template>
                </div>
            </div>
            <!-- Splitters are grid-owned overlays rather than window children: one
                 boundary can span several differently arranged windows, and moving it
                 must update the shared track rather than whichever pane was clicked. -->
            <button
                v-for="splitter in column_splitters"
                :key="'column-splitter-' + splitter.index + '-' + splitter.segment_index"
                type="button"
                class="layout-grid-splitter layout-grid-splitter-column"
                :class="{ 'is-resizing': isGridSplitterActive( 'column', splitter.index ) }"
                :style="gridSplitterStyle( 'column', splitter )"
                role="separator"
                aria-orientation="vertical"
                :aria-label="getGridSplitterLabel( 'column', splitter.index )"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="Math.round( splitter.position )"
                data-tt-widget="window-grid-splitter"
                data-tt-axis="column"
                :data-tt-boundary-index="splitter.index"
                @pointerdown="startGridResize( 'column', splitter.index, $event )"
                @pointermove="onGridResizePointerMove( 'column', splitter.index, $event )"
                @pointerup="finishGridResize( $event )"
                @pointercancel="finishGridResize( $event )"
                @lostpointercapture="finishGridResize( $event )"
                @keydown="onGridResizeKeyDown( 'column', splitter.index, $event )"
            ></button>
            <button
                v-for="splitter in row_splitters"
                :key="'row-splitter-' + splitter.index + '-' + splitter.segment_index"
                type="button"
                class="layout-grid-splitter layout-grid-splitter-row"
                :class="{ 'is-resizing': isGridSplitterActive( 'row', splitter.index ) }"
                :style="gridSplitterStyle( 'row', splitter )"
                role="separator"
                aria-orientation="horizontal"
                :aria-label="getGridSplitterLabel( 'row', splitter.index )"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="Math.round( splitter.position )"
                data-tt-widget="window-grid-splitter"
                data-tt-axis="row"
                :data-tt-boundary-index="splitter.index"
                @pointerdown="startGridResize( 'row', splitter.index, $event )"
                @pointermove="onGridResizePointerMove( 'row', splitter.index, $event )"
                @pointerup="finishGridResize( $event )"
                @pointercancel="finishGridResize( $event )"
                @lostpointercapture="finishGridResize( $event )"
                @keydown="onGridResizeKeyDown( 'row', splitter.index, $event )"
            ></button>
            <!-- drop indicator -->
            <div
                v-if="has_drop_indicator"
                class="layout-drop-indicator"
                :class="drag.over_intent || 'cell'"
                :style="gridIndicatorVars()"
            ></div>
        </div>
    </div>
</template>
<script>
import { RouteSurfacePolicy } from '@/components/RouteSurfacePolicy';
import { TTWindowLayoutPolicy } from '@/components/TTWindowLayoutPolicy';
import TTWindowCaptionButton from '@/components/TTWindowCaptionButton.vue';
import { useTTViewStackStore } from '@/stores/TTViewStack';

export default {
    name: 'TTWindowManager',
    // The authenticated parent persists this shared preference; direct/anonymous
    // shells have no view-options menu and never invoke this event.
    emits: ['save-open-records-in'],
    components: {
        TTWindowCaptionButton,
    },
    // This mode is deliberately independent of Pinia and authenticated services:
    // password recovery must work before either a session or a view stack exists.
    props: {
        direct_shell: { type: Object, default: null },
        direct_component: { type: Object, default: null },
        direct_props: { type: Object, default: () => ( {} ) },
        component_id: { type: String, default: null },
    },
    provide() {
        return {
            tt_window_manager: {
                setEditLaneCount: ( id, lane_count, source_id, options ) => this.setEditLaneCount( id, lane_count, source_id, options ),
                // Wizard TTS owns its outer card dimensions; keeping this at the window manager prevents an authored child from shrinking inside a still-full-size white modal canvas.
                setWizardShellLayout: ( id, layout ) => this.setWizardShellLayout( id, layout ),
                setWizardShellHeight: ( id, shell_height ) => this.setWizardShellHeight( id, shell_height ),
                // Routed custom owners publish presentation and processing only;
                // the manager retains the actual size and caption-close policy.
                setShellLayout: ( id, layout ) => this.setShellLayout( id, layout ),
                setWindowCloseBlocked: ( id, is_blocked ) => this.setWindowCloseBlocked( id, is_blocked ),
                // EditRecordsTabbed cannot safely mutate shell CSS directly because
                // TTWindowManager owns the locked-height caches and responsive mode
                // variables. Keep tab-driven sizing requests behind this manager API
                // so the same grow-only rule applies to generic and lane-specific
                // modal heights.
                setEditShellMinHeight: ( id, shell_min_height ) => this.setEditShellMinHeight( id, shell_min_height ),
                // TTWizard owns the workflow header and Close policy, but delegates
                // the Controls menu/drag behavior back to this shell manager.
                getWindowMenuId: id => this.getWindowMenuId( id ),
                getWindowMenuLabel: id => this.getWindowMenuLabel( this.tt_view_stack.getViewById( id ) ),
                getWindowMenuActionLabel: () => this.labels.window_tooltip,
                isWindowMenuOpen: id => this.menu.active_id === id,
                setActiveWindow: id => this.setActiveWindow( id ),
                toggleWindowMenu: id => this.toggleWindowMenu( id ),
                onWindowDragStart: ( event, id ) => this.onWindowDragStart( event, id ),
                onWindowDragEnd: () => this.onWindowDragEnd(),
                // Teleport preserves this Vue provider but moves picker DOM outside
                // the window. Register the rendered root against its in-window anchor.
                registerWindowPortal: ( root, anchor, dismiss, modal ) => this.registerWindowPortal( root, anchor, dismiss, modal ),
                unregisterWindowPortal: root => this.unregisterWindowPortal( root ),
                focusWindowPortal: root => this.focusWindowPortal( root ),
                // Issue #3831: Validation actions rejoin the owner's form using the same native focus
                // eligibility as window boundaries, without admitting picker display inputs.
                getWindowFocusableElements: root => this.getWindowFocusableElements( root ),
            },
        };
    },
    data() {
        return {
            tt_view_stack: this.direct_shell ? null : useTTViewStackStore(),
            direct_processing: false,
            labels: {
                window_controls: TTi18n.getText( 'Window Controls' ),
                window_tooltip: TTi18n.getText( 'Window Controls (drag to move)' ),

                open_window: TTi18n.getText( 'Show Hidden Views' ),
                pop_in: TTi18n.getText( 'Move to Main View' ),
                pop_out: TTi18n.getText( 'Float Window' ),
                pin_right: TTi18n.getText( 'Dock Right' ),
                minimize_view: TTi18n.getText( 'Minimize' ),
                close_view: TTi18n.getText( 'Close' ),
                resize_columns: TTi18n.getText( 'Resize columns %1 and %2' ),
                resize_rows: TTi18n.getText( 'Resize rows %1 and %2' ),

                default_view: TTi18n.getText( 'View' ),
            },

            menu: {
                active_id: null,
                show_hidden_list: false,
            },

            drag: {
                dragging_id: null,
                over_cell: null,      // { x, y } for grid background
                over_window_id: null, // window id when hovering a window
                over_intent: null,    // 'swap' | 'col-left' | 'col-right' | 'row-above' | 'row-below'
            },
            grid_resize: {
                axis: null,
                boundary_index: null,
                pointer_id: null,
                start_coordinate: 0,
                start_sizes: null,
                total_pixels: 0,
            },
            // Presentation only: changing pane confinement must never change the router render key.
            float_within_pane: {},
            edit_lane_counts: {},
            locked_edit_lane_counts: {},
            edit_shell_heights: {},
            locked_edit_shell_heights: {},
            edit_shell_heights_by_mode: {},
            locked_edit_shell_heights_by_mode: {},
            edit_shell_widths: {},
            locked_edit_shell_widths: {},
            wizard_shell_heights: {},
            wizard_shell_widths: {},
            content_sized_wizard_shells: {},
            // This cache records a declarative capability supplied with prepaint
            // metadata, not a live DOM sizing result. Automatic ordinary edit forms
            // may grow intrinsically to the viewport cap; explicitly sized workspaces
            // remain exact. Keep the mode keyed to the window render signature just
            // like lane/height locks so a reused stack id cannot inherit it.
            content_sized_edit_shells: {},
            // Standalone TTSShell dimensions and busy state are kept outside the
            // edit-lane caches so the new peer surface cannot inherit CRUD sizing.
            shell_layouts: {},
            window_close_blocked: {},
            shell_focus_return_target: null,
            // A child Wizard can cover another routed dialog. Keep each launcher's
            // target until that exact view leaves the stack, not until focus changes.
            modal_focus_return_targets: new Map(),
            // Track only mounted body roots; stale picker DOM must never remain
            // eligible after its widget hides or unmounts.
            window_portals: new Map(),
            window_render_signatures: {},
            auto_fill_enabled: true,
        };
    },
    computed: {
        views() {
            return this.direct_shell ? [] : this.tt_view_stack.view_stack;
        },
        active_view_id() {
            return this.direct_shell ? null : this.tt_view_stack.active_view_id;
        },
        visible_views() {
            return this.views.filter( v => v.visible );
        },
        hidden_views() {
            return this.views.filter( v => !v.visible );
        },
        // Reflow must be driven by view identity, not by array length. The visible
        // symptom is easiest to reproduce with three panes: close the middle pane,
        // then observe the surviving right pane remain separated by a blank column.
        // Some router transitions remove one view and add its replacement in the
        // same Vue batch, so the array length can stay unchanged even though a grid
        // occupant disappeared. This immutable ID snapshot preserves that event for
        // the watcher without making ordinary geometry edits look like removals.
        view_ids() {
            return this.views.map( view => view.id );
        },
        active_modal_view_id() {
            const active_view = this.active_view_id ? this.tt_view_stack.getViewById( this.active_view_id ) : null;

            return active_view?.visible === true && this.isModalWindow( active_view.id ) ? active_view.id : null;
        },
        // Keep the CSS grid and ARIA exclusivity decisions on the same ancestry predicate. If
        // these diverge, a pane can remain visually present while being inert/hidden to automation.
        has_active_modal_side_panel() {
            return !!this.active_modal_view_id
                && this.visible_views.some( view => this.isWindowActiveModalCompanion( view ) );
        },
        has_drop_indicator() {
            return !!this.drag.dragging_id && ( !!this.drag.over_cell || ( !!this.drag.over_window_id && !!this.drag.over_intent ) );
        },
        window_state_signature() {
            return this.views.map( view => {
                return [
                    view.id,
                    this.getWindowRenderKey( view ),
                    view.visible === true ? 'visible' : 'hidden',
                ].join( ':' );
            } ).join( '|' );
        },
        grid_windows() {
            // Keep hidden views mounted so returning to them restores the existing component
            // instance instead of recreating it from scratch.
            return this.views
                .map( ( v, order ) => {
                    // Issue #3934: A floating window can retain its former docked cell or full-width span.
                    // Companion placement resizes its primary ancestor, so render the modal over that current
                    // cell rather than covering the Assistant. This covers Wizards, routed shells and route-less tasks;
                    // record editors retain their separate Float Within Pane geometry, and docked/main windows keep their cells.
                    // A missing or hidden primary cannot supply visible bounds; retain the modal's stored cell in that case.
                    const source_view = this.isModalWindow( v.id ) && !this.requiresEditLaneSizing( v.id ) && this.has_active_modal_side_panel
                        ? this.tt_view_stack.getPrimaryAncestorView( v )
                        : null;
                    const rect = TTWindowLayoutPolicy.getViewRect( source_view?.visible === true ? source_view : v );

                    return { window: v, order, x: rect.x, y: rect.y, w: rect.w, h: rect.h };
                } )
                .sort( ( a, b ) => ( a.y - b.y ) || ( a.x - b.x ) || ( a.order - b.order ) );
        },
        grid_col_count() {
            let max = 1;
            this.getOccupiedLayoutViews().forEach( v => {
                const rect = TTWindowLayoutPolicy.getViewRect( v );

                max = Math.max( max, rect.x + rect.w );
            } );
            return Math.max( 1, max );
        },
        grid_row_count() {
            let max = 1;
            this.getOccupiedLayoutViews().forEach( v => {
                const rect = TTWindowLayoutPolicy.getViewRect( v );

                max = Math.max( max, rect.y + rect.h );
            } );
            return Math.max( 1, max );
        },
        // Issue #3864: Resize permission is narrower than render/ARIA permission.
        // This projection feeds splitter segmentation only; it must not remove
        // covered panes from the actual layout or change their stored rectangles.
        // Without a modal, existing arbitrary row/column arrangements are unchanged.
        // With a modal, only the single confined source/companion pair is eligible.
        // Its source is a nonmodal grid occupant beneath the editor, not the editor
        // overlay itself; changing that source track resizes the confined editor too.
        grid_resize_views() {
            const occupied_views = this.getOccupiedLayoutViews();
            if ( !this.active_modal_view_id ) {
                return occupied_views;
            }

            // Issue #3864: Edit Template -> Preview -> Dock Right leaves the edit
            // modal active beside its usable companion. Allow their shared divider,
            // but never resize obscured/unrelated panes through a modal backdrop.
            // Companion ARIA eligibility alone is broader than resize authority:
            // an unconfined edit or extra grid occupants must retain the modal block.
            const modal = this.tt_view_stack.getViewById( this.active_modal_view_id );
            // getWindowSourcePane already checks record-modal eligibility and walks
            // nested parents to a visible nonmodal launcher with cycle protection.
            // Do not substitute the primary ancestor: a docked editor can be the
            // actual source, and a hidden ancestor cannot supply painted bounds.
            const source = this.getWindowSourcePane( modal );
            const companions = occupied_views.filter( view => this.isSidePanelWindow( view ) && this.isWindowActiveModalCompanion( view ) );
            // All checks protect shared tracks, not just the visible divider:
            // * missing source/non-record modal: no established confinement bounds;
            // * Float Within Pane off: the overlay can span the proposed boundary;
            // * zero/multiple companions: there is no unique two-pane resize pair;
            // * extra occupants: one global track change could resize covered panes.
            // Failure keeps the historical modal block instead of guessing a pair.
            if ( !source || this.float_within_pane[modal.id] === false || companions.length !== 1
                || occupied_views.some( view => view.id !== source.id && view.id !== companions[0].id ) ) {
                return [];
            }
            // The permitted source/companion still use their existing rectangles.
            // getGridSplitterSegments emits only edges they actually share, never
            // a full-height hit strip through spanning or empty cells.
            return occupied_views;
        },
        column_splitters() {
            // Segment generation uses the same permitted occupants as both input paths.
            return this.getGridSplitterSegments( 'column' );
        },
        row_splitters() {
            return this.getGridSplitterSegments( 'row' );
        },
        gridStyle() {
            const col_count = this.grid_col_count;
            const row_count = this.grid_row_count;

            const style = {
                '--col-count': col_count,
                '--row-count': row_count,
            };

            const col_sizes = this.tt_view_stack.layout_grid?.col_sizes;
            const col_template = TTWindowLayoutPolicy.getTrackTemplate( col_sizes, col_count );
            if ( col_template ) {
                style['--col-template'] = col_template;
            }

            const row_sizes = this.tt_view_stack.layout_grid?.row_sizes;
            const row_template = TTWindowLayoutPolicy.getTrackTemplate( row_sizes, row_count );
            if ( row_template ) {
                style['--row-template'] = row_template;
            }
            return style;
        },
    },
    watch: {
        view_ids( new_ids, old_ids ) {
            if ( Array.isArray( old_ids ) !== true || old_ids.some( id => new_ids.includes( id ) !== true ) !== true ) {
                return;
            }

            // Detect removed identities rather than a count decrease. This covers
            // caption Close, menu Close, minimized-tab Close, a record Cancel path,
            // and router-owned replacement through one shared stack observation.
            // Calling reflow only from the window-manager buttons looked sufficient,
            // but left every direct removeView() caller capable of recreating the gap.
            //
            // Wait for Vue's normal batch because removeView() still has to select a
            // fallback active view and a router may add a replacement in that batch.
            // Compacting synchronously can measure a half-updated stack, while using
            // the ID snapshot above still detects a remove/add replacement whose final
            // item count matches the old count.
            this.$nextTick( () => {
                this.applyAutoFillIfEnabled();
                // Stack trimming can evict a covered parent Wizard without changing
                // active_modal_view_id. Reconcile after its watcher has captured
                // any closing foreground view's launcher for focus restoration.
                for ( const id of this.modal_focus_return_targets.keys() ) {
                    if ( this.view_ids.includes( id ) !== true ) {
                        this.modal_focus_return_targets.delete( id );
                    }
                }
            } );
        },
        window_state_signature: {
            immediate: true,
            handler() {
                this.syncWindowStateCaches();
            },
        },
        active_modal_view_id( new_id, old_id ) {
            // A closed view is already absent from TTViewStack when this watcher
            // runs, so its route action can no longer classify it. The saved
            // launcher entry is the durable evidence that it was managed.
            const old_managed = old_id && this.modal_focus_return_targets.has( old_id );
            const new_managed = new_id && this.isManagedFocusWindow( new_id );
            const old_view = old_id ? this.tt_view_stack.getViewById( old_id ) : null;
            // TTViewStack hides a covered parent modal without removing it.
            // Preserve that parent's launcher through the child; release only
            // when this view is removed or no modal replaces its active slot.
            const old_left_foreground = old_managed && ( !old_view || !new_id );
            const return_target = old_left_foreground ? this.modal_focus_return_targets.get( old_id ) : null;
            if ( old_left_foreground ) {
                this.modal_focus_return_targets.delete( old_id );
            }

            if ( new_managed && !this.modal_focus_return_targets.has( new_id ) ) {
                this.modal_focus_return_targets.set( new_id, document.activeElement instanceof HTMLElement ? document.activeElement : null );
            }

            if ( old_left_foreground || new_managed ) {
                this.$nextTick( () => {
                    if ( return_target instanceof HTMLElement && return_target.isConnected && !return_target.closest( '[inert], [aria-hidden="true"]' ) ) {
                        return_target.focus();
                    } else if ( new_managed ) {
                        this.focusManagedWindow( new_id );
                    } else if ( old_left_foreground ) {
                        // A transient menu item may have launched the Wizard and
                        // disappeared on route change. Return to the revealed view
                        // instead of leaving focus on the removed dialog/body.
                        const revealed_window = this.$el?.querySelector?.( '.layout-window[data-tt-active-view="true"]' );
                        if ( revealed_window instanceof HTMLElement && !revealed_window.contains( document.activeElement ) ) {
                            ( this.getWindowFocusableElements( revealed_window )[0] || revealed_window ).focus();
                        }
                    }
                } );
            }
        },
    },

    mounted() {
        // Bubble-phase Tab runs after widget handlers that intentionally collapse
        // a picker on Tab; capture-phase Escape runs before the global Wizard-close
        // shortcut so a registered foreground picker always dismisses first.
        document.addEventListener( 'keydown', this.onDocumentCaptureKeydown, true );
        document.addEventListener( 'keydown', this.onDocumentBubbleKeydown );
        if ( this.direct_shell ) {
            this.shell_focus_return_target = document.activeElement instanceof HTMLElement ? document.activeElement : null;
            this.$nextTick( () => this.focusManagedWindow() );
        }
    },
    beforeUnmount() {
        document.removeEventListener( 'keydown', this.onDocumentCaptureKeydown, true );
        document.removeEventListener( 'keydown', this.onDocumentBubbleKeydown );
        this.window_portals.clear();
        // Restore synchronously so a same-mount MFA/reset handoff can capture the
        // original launcher before the new owner's mounted hook focuses its input.
        if ( this.direct_shell && this.shell_focus_return_target?.isConnected === true ) {
            this.shell_focus_return_target.focus();
        }
    },
    methods: {
        getWindowRenderKey( window_data ) {
            const window_id = window_data?.id ?? '';
            const path_key = this.getWindowRenderPathKey( window_data?.path, window_data?.tt_route_data );
            const route_revision = Number.isInteger( window_data?.route_revision ) ? window_data.route_revision : 0;

            return window_id + ':' + path_key + ':' + route_revision;
        },
        // The render key is the hard Vue remount boundary. Keep this delegated to
        // RouteSurfacePolicy so TTWindowManager and TTRouter agree on which URL
        // params are live mounted-state versus structural route identity.
        getWindowRenderPathKey( path, route_data = null ) {
            return RouteSurfacePolicy.getWindowRenderPathKey( path, route_data );
        },
        getWindowMenuItems( window_data ) {
            const w = window_data?.window ?? null;
            const id = w?.id ?? null;
            const is_modal = w ? this.isModalWindow( w.id ) : false;

            // Routed surfaces share this menu, including the account-wide opening
            // preference. Keep hidden-view recovery last; Close stays on the caption X.
            return [
                {
                    type: 'button',
                    key: is_modal ? 'pop-in' : 'pop-out',
                    icon: is_modal ? 'tticon-view_module_black_24dp' : 'tticon-north_east_black_24dp',
                    label: is_modal ? this.labels.pop_in : this.labels.pop_out,
                    // Float uses the existing in-app modal, not a separate browser window.
                    title: is_modal ? undefined : TTi18n.getText( 'Display this view in a floating window within TimeTrex.' ),
                    data_action: is_modal ? 'window-pop-in' : 'window-pop-out',
                    onClick: () => this.handleWindowMenuAction( is_modal ? 'popIn' : 'popOut', id ),
                },
                {
                    type: 'button',
                    // A docked panel needs a direct return to the main view. Reuse
                    // the dialog promotion action/hook so automation and mounted
                    // editors share the same transition without an intermediate modal.
                    key: this.isSidePanelWindow( w ) ? 'pop-in' : 'pin-right',
                    icon: this.isSidePanelWindow( w ) ? 'tticon-view_module_black_24dp' : 'tticon-arrow_forward_black_24dp',
                    label: this.isSidePanelWindow( w ) ? this.labels.pop_in : this.labels.pin_right,
                    disabled: !this.isSidePanelWindow( w ) && !this.canPinWindowRight( w ),
                    data_action: this.isSidePanelWindow( w ) ? 'window-pop-in' : 'window-pin-right',
                    onClick: () => this.handleWindowMenuAction( this.isSidePanelWindow( w ) ? 'popIn' : 'pinRight', id ),
                },
                {
                    type: 'button',
                    key: 'auto-pin',
                    label: TTi18n.getText( 'Open records in Right Panel' ),
                    title: TTi18n.getText( 'When off, records open in a dialog. Applies to main lists.' ),
                    // Keep the existing checkbox and automation hook, but project only
                    // the saved value. Disable all copies while loading or saving.
                    checked: this.tt_view_stack.auto_pin_enabled === true,
                    disabled: !this.tt_view_stack.window_settings_loaded || this.tt_view_stack.window_settings_busy,
                    data_action: 'window-auto-pin-toggle',
                    onClick: () => this.handleWindowMenuAction( 'toggleAutoPin', id ),
                },
                // Only record floating windows expose this local lifetime preference.
                // A single/missing source pane disables the same discoverable checkbox;
                // its stable action hook is shared by pointer, keyboard and automation.
                ...( is_modal && this.requiresEditLaneSizing( id ) ? [{
                    type: 'button',
                    key: 'float-within-pane',
                    label: TTi18n.getText( 'Float Within Pane' ),
                    title: TTi18n.getText( 'Keep this floating window within the pane it opened from.' ),
                    checked: this.float_within_pane[id] !== false,
                    disabled: !this.getWindowSourcePane( w ),
                    data_action: 'window-float-within-pane-toggle',
                    onClick: () => this.handleWindowMenuAction( 'toggleFloatWithinPane', id ),
                }] : [] ),
                { type: 'divider', key: 'div-hidden' },
                {
                    type: 'submenu',
                    key: 'open-window',
                    class: 'window-menu-item-open',
                    icon: 'tticon-add_black_24dp',
                    label: this.labels.open_window,
                    data_action: 'window-open-menu',
                    disabled: this.hidden_views.length === 0,
                    onClick: () => this.toggleHiddenWindowList(),
                    show: this.menu.show_hidden_list && this.hidden_views.length,
                    children: this.hidden_views.map( view => ( {
                        key: 'open-hidden-' + view.id,
                        icon: 'tticon-view_module_black_24dp',
                        label: this.getViewLabel( view ),
                        data_action: 'window-open-hidden',
                        view_id: view.id,
                        // Pass the menu owner rather than consulting active_view_id in
                        // openHiddenView(). Repro: keep two primary panes visible, make
                        // the right pane active, then open Show Hidden Views from the
                        // left pane. The selected hidden view must replace the left pane,
                        // not whichever pane was most recently active. The explicit ID
                        // also survives closeMenus(), which intentionally clears the only
                        // other durable record of which window owned this submenu.
                        onClick: () => this.openHiddenView( view.id, id ),
                    } ) ),
                },
            ];
        },
        getWindowMenuId( id ) {
            return 'tt-window-menu-' + id;
        },
        getWindowMenuLabel( view ) {
            return `${this.labels.window_tooltip}: ${this.getViewLabel( view )}`;
        },
        getWindowControlsLabel( view ) {
            return `${this.labels.window_controls}: ${this.getViewLabel( view )}`;
        },
        getWindowActionLabel( action_label, view ) {
            return `${action_label}: ${this.getViewLabel( view )}`;
        },
        closeMenus() {
            this.menu.active_id = null;
            this.menu.show_hidden_list = false;
        },
        onGridEscape( event ) {
            if ( !this.menu.active_id ) {
                return;
            }

            // The Controls menu is the topmost local keyboard surface. Claim Escape
            // before TTShortcutRegistry's bubbling application fallback can interpret
            // the same key as Cancel for the editor underneath. This also keeps focus
            // on the Controls trigger, matching the menu's existing pointer close path.
            event.preventDefault();
            event.stopPropagation();
            this.closeMenus();
        },
        getWindowFocusableElements( window_element ) {
            // Query each owned DOM root separately: a body Teleport is not a
            // descendant of the routed dialog even though it is a Vue child.
            if ( !( window_element instanceof HTMLElement ) ) {
                return [];
            }

            // Disabled, negative-tabindex, covered, and visually hidden controls
            // cannot participate in native sequential focus. Including one as a
            // boundary would strand Tab on a non-focusable target.
            return Array.from( window_element.querySelectorAll( 'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]' ) )
                .filter( element => element.tabIndex >= 0
                    && element.matches( ':disabled' ) !== true
                    && element.getClientRects().length > 0
                    && element.closest( '[inert], [aria-hidden="true"]' ) === null
                    && getComputedStyle( element ).visibility !== 'hidden' );
        },
        getManagedWindowElement( id ) {
            // Reuse the rendered window boundary for initial focus and the
            // document-level Tab path; the view stack contains data, not DOM.
            return Array.from( this.$el?.querySelectorAll?.( '.layout-window[data-tt-view-id]' ) || [] )
                .find( element => element.getAttribute( 'data-tt-view-id' ) === String( id ) ) ?? null;
        },
        focusManagedWindow( id ) {
            if ( this.direct_shell ) {
                // Owner-specific initial input focus wins over the dialog fallback.
                if ( !this.$refs.direct_dialog?.contains( document.activeElement ) ) {
                    this.$refs.direct_dialog?.focus();
                }
                return true;
            }
            if ( !id || id !== this.active_modal_view_id || !this.isManagedFocusWindow( id ) ) {
                return false;
            }

            const window_element = this.getManagedWindowElement( id );
            if ( !( window_element instanceof HTMLElement ) ) {
                return false;
            }

            // A Wizard may select its own initial control during mount. Do not
            // replace that valid focus; only recover from an outside launcher.
            if ( window_element.contains( document.activeElement ) || this.getActiveWindowPortals( id ).some( portal => portal.root.contains( document.activeElement ) ) ) {
                return true;
            }
            const first_element = this.getWindowFocusableElements( window_element )[0] || window_element;
            first_element.focus();
            return true;
        },
        registerWindowPortal( root, anchor, dismiss = null, modal = false ) {
            // Direct authentication shells keep their own local trap. A picker
            // without both rendered root and stable owner anchor cannot safely
            // join a routed dialog's keyboard sequence.
            if ( this.direct_shell || !( root instanceof HTMLElement ) || !( anchor instanceof HTMLElement ) ) {
                return false;
            }

            // A nested picker can anchor in another body Teleport. Resolve its
            // logical window through the registered parent instead of DOM ancestry.
            const window_id = anchor.closest( '.layout-window[data-tt-view-id]' )?.getAttribute( 'data-tt-view-id' );
            const parent_portal = Array.from( this.window_portals.values() ).find( portal => portal.root.contains( anchor ) );
            const owner_id = window_id || parent_portal?.owner_id;
            // Non-Wizard fields and unrelated body overlays retain their own
            // keyboard behavior; never attach them to an arbitrary active modal.
            if ( !owner_id || !this.isManagedFocusWindow( owner_id ) ) {
                return false;
            }

            const portal = { root, anchor, owner_id, dismiss, modal };
            this.window_portals.set( root, portal );
            if ( modal === true && owner_id === String( this.active_modal_view_id ) ) {
                // A nested modal replaces its launcher's picker panel. Focus its
                // first control after Teleport paints, before keyboard navigation.
                this.$nextTick( () => {
                    // Vue proxies Map values in component data, so compare the
                    // stable DOM-key presence rather than object identity here.
                    if ( this.window_portals.has( root ) ) {
                        this.getWindowFocusableElements( root )[0]?.focus();
                    }
                } );
            }
            return true;
        },
        focusWindowPortal( root ) {
            const portal = this.window_portals.get( root );
            // Only a mounted picker belonging to the foreground routed window
            // may claim focus. Standalone and covered pickers keep their trigger.
            if ( !portal || portal.owner_id !== String( this.active_modal_view_id ) || root.isConnected !== true ) {
                return false;
            }
            const first_element = this.getWindowFocusableElements( root )[0];
            if ( !( first_element instanceof HTMLElement ) ) {
                return false;
            }
            first_element.focus();
            return true;
        },
        unregisterWindowPortal( root ) {
            const portal = this.window_portals.get( root );
            // Restore only when the nested modal currently owns focus. Pointer
            // dismissal may already have chosen a new destination outside it.
            const restore_focus = portal?.modal === true && root?.contains?.( document.activeElement );
            this.window_portals.delete( root );
            if ( restore_focus ) {
                this.$nextTick( () => {
                    // The parent AwesomeBox's old panel may be gone, but its field
                    // anchor remains in the routed dialog after selector Close.
                    const target = portal.anchor?.isConnected ? this.getWindowFocusableElements( portal.anchor )[0] : null;
                    target?.focus();
                } );
            }
        },
        getActiveWindowPortals( id ) {
            // Sort by the actual painted layer, then DOM order, so Escape sees
            // the foreground picker while Tab visits every owner-related root.
            return Array.from( this.window_portals.values() )
                .filter( portal => portal.owner_id === String( id )
                    && portal.root.isConnected === true
                    && portal.root.getClientRects().length > 0
                    && portal.root.closest( '[inert], [aria-hidden="true"]' ) === null )
                .sort( ( first, second ) => {
                    const z_difference = ( Number.parseInt( getComputedStyle( first.root ).zIndex, 10 ) || 0 )
                        - ( Number.parseInt( getComputedStyle( second.root ).zIndex, 10 ) || 0 );
                    return z_difference || ( first.root.compareDocumentPosition( second.root ) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1 );
                } );
        },
        hasForegroundBodyDialog() {
            // TTModalShell, Feedback, and legacy confirmations can cover a Wizard
            // from body without using its portal registration. Those dialogs own
            // their own keyboard policy; never re-enter or dismiss the Wizard
            // beneath one. Match the modal markers used by shortcut arbitration.
            return Array.from( document.querySelectorAll( '[data-tt-modal], [role="dialog"][aria-modal="true"]' ) )
                .some( element => element.closest( '.layout-window' ) === null
                    && element.getClientRects().length > 0
                    && element.closest( '[inert], [aria-hidden="true"]' ) === null );
        },
        onDocumentCaptureKeydown( event ) {
            if ( event.key !== 'Escape' || event.defaultPrevented === true || this.direct_shell || this.hasForegroundBodyDialog() ) {
                return;
            }

            const id = this.active_modal_view_id;
            if ( !id || !this.isManagedFocusWindow( id ) ) {
                return;
            }

            // TTOverlayPanel owns its own topmost capture-phase Escape. A direct
            // calendar/column portal supplies dismissal here so Escape cannot
            // reach the Wizard's document shortcut first, even from its footer.
            const foreground_portal = this.getActiveWindowPortals( id ).at( -1 );
            if ( typeof foreground_portal?.dismiss !== 'function' ) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            foreground_portal.dismiss();
        },
        onDocumentBubbleKeydown( event ) {
            if ( event.key !== 'Tab' || event.defaultPrevented === true || this.direct_shell || this.hasForegroundBodyDialog() ) {
                return;
            }

            const id = this.active_modal_view_id;
            const view = id ? this.tt_view_stack.getViewById( id ) : null;
            // A companion Assistant pane is deliberately interactive; mirror the
            // same exclusivity predicate used by the dialog's aria-modal value.
            if ( !view || !this.isManagedFocusWindow( id ) || !this.isWindowExclusiveActiveModal( view ) ) {
                return;
            }

            const window_element = this.getManagedWindowElement( id );
            if ( !( window_element instanceof HTMLElement ) ) {
                return;
            }

            const portals = this.getActiveWindowPortals( id );
            const foreground_modal = portals.filter( portal => portal.modal === true ).at( -1 );
            let focus_roots = [window_element, ...portals.map( portal => portal.root )];
            if ( foreground_modal ) {
                // An AwesomeBox column selector masks the Wizard. Keep keyboard
                // focus in that nested modal and any of its own child Teleports.
                const nested_roots = [foreground_modal.root];
                portals.forEach( portal => {
                    if ( portal !== foreground_modal && nested_roots.some( root => root.contains( portal.anchor ) ) ) {
                        nested_roots.push( portal.root );
                    }
                } );
                focus_roots = nested_roots;
            }
            const groups = focus_roots
                .map( root => ( { root, elements: this.getWindowFocusableElements( root ) } ) )
                .filter( group => group.root === focus_roots[0] || group.elements.length > 0 );
            // An API-busy or empty Wizard still needs a focusable boundary, or
            // native Tab would escape into the covered navigation.
            if ( groups[0].elements.length === 0 ) {
                groups[0].elements = [focus_roots[0]];
            }

            const active_element = document.activeElement;
            const group_index = groups.findIndex( group => group.root.contains( active_element ) );
            const group = groups[group_index];
            // If a widget synchronously removed its portal in its own Tab handler,
            // re-enter the surviving dialog instead of following detached DOM.
            if ( !group ) {
                event.preventDefault();
                const destination = event.shiftKey ? groups.at( -1 ).elements.at( -1 ) : groups[0].elements[0];
                destination.focus();
                return;
            }

            const active_index = group.elements.indexOf( active_element );
            // Programmatic focus on a dialog/root is outside native Tab order;
            // enter its first/last control. Only group edges need intervention:
            // interior steps remain native for widget-specific keyboard logic.
            if ( active_element === group.root || active_index < 0 ) {
                event.preventDefault();
                ( event.shiftKey ? group.elements.at( -1 ) : group.elements[0] ).focus();
            } else if ( event.shiftKey && active_index === 0 ) {
                event.preventDefault();
                groups[( group_index + groups.length - 1 ) % groups.length].elements.at( -1 ).focus();
            } else if ( !event.shiftKey && active_index === group.elements.length - 1 ) {
                event.preventDefault();
                groups[( group_index + 1 ) % groups.length].elements[0].focus();
            }
        },
        onWindowKeydown( event, view ) {
            if ( event.defaultPrevented === true || ( !this.direct_shell && ( !view || this.isTTSShellWindow( view.id ) !== true || view.id !== this.active_modal_view_id ) ) ) {
                return;
            }

            if ( event.key === 'Escape' ) {
                // Issue #3864: Enabled shells share Payroll's menu-first dismissal. The shell's
                // local Escape handler runs before the grid handler, so dismissing
                // an open Controls menu must not also close its owning task.
                if ( !this.direct_shell && this.menu.active_id === view.id ) {
                    this.closeMenus();
                    // Closing the menu removes its focused option. Return focus to
                    // the owning caption after that DOM update, and consume Escape
                    // here so the grid handler cannot also close the retained task.
                    this.$nextTick( () => this.getManagedWindowElement( view.id )?.querySelector( '[data-tt-action="window-menu"]' )?.focus() );
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }
                // A standalone shell is the foreground keyboard owner. Claim Escape
                // here so it cannot reach the covered application, and let the same
                // processing boundary used by caption Close decide whether it closes.
                event.preventDefault();
                event.stopPropagation();
                if ( this.direct_shell ) {
                    // Close policy and completion remain owner methods, including
                    // reset-link cleanup and cancellation of the owner's MFA polls.
                    this.requestCloseDirectShell();
                } else if ( this.isWindowCloseBlocked( view.id ) !== true ) {
                    this.closeWindow( view.id );
                }
                return;
            }

            // Routed dialogs use the document boundary above so teleported picker
            // events are visible. Only the standalone authentication shell needs
            // this local Tab path because it has no routed view/provider.
            if ( event.key !== 'Tab' || !this.direct_shell ) {
                return;
            }

            // Shells do not introduce a nested modal/focus component, so wrap Tab
            // at this existing routed-window boundary in both directions.
            const window_element = event.currentTarget;
            const focusable_elements = this.getWindowFocusableElements( window_element );
            if ( focusable_elements.length === 0 ) {
                event.preventDefault();
                window_element?.focus?.();
                return;
            }

            const first_element = focusable_elements[0];
            const last_element = focusable_elements[focusable_elements.length - 1];
            if ( event.shiftKey === true && ( document.activeElement === window_element || document.activeElement === first_element || !window_element.contains( document.activeElement ) ) ) {
                event.preventDefault();
                last_element.focus();
            } else if ( event.shiftKey === false && document.activeElement === last_element ) {
                event.preventDefault();
                first_element.focus();
            }
        },
        requestCloseDirectShell() {
            // Issue #3768: Escape still dismisses a pending authentication dialog
            // after its temporary status/Close card is removed; the owner keeps its veto.
            if ( this.$refs.direct_content?.canCloseView?.() !== false ) {
                this.$refs.direct_content?.close();
            }
        },
        onDirectWindowFirstPaintReady() {
            // Issue #3768: the boundary owns keyboard focus during the invisible
            // first paint; move it to the form only after the owner reveals controls.
            this.$nextTick( () => {
                const dialog = this.$refs.direct_dialog;
                // Closing or replacing the direct shell in the same tick invalidates
                // the event; do not focus a detached login/MFA dialog.
                if ( !dialog || !this.direct_shell ) {
                    return;
                }
                const focused = document.activeElement;
                // Preserve a field chosen by the owner or user.
                if ( focused === dialog ) {
                    ( this.getWindowFocusableElements( dialog )[0] || dialog ).focus();
                }
            } );
        },
        onWindowFirstPaintReady( id ) {
            // Issue #3768: only move manager-owned focus from the modal boundary
            // after the routed owner has revealed its complete first paint.
            this.$nextTick( () => {
                // Another window can become foreground before Vue applies ready.
                // A stale event must never pull focus out of that newer modal.
                if ( id !== this.active_modal_view_id ) {
                    return;
                }
                const window_element = this.getManagedWindowElement( id );
                const focused = document.activeElement;
                // EditRecords may claim its preferred schema input in its own
                // follow-up frame. This fallback only rescues manager-owned focus.
                if ( window_element && focused === window_element ) {
                    ( this.getWindowFocusableElements( window_element )[0] || window_element ).focus();
                }
            } );
        },
        toggleWindowMenu( id ) {
            if ( this.menu.active_id === id ) {
                this.closeMenus();
                return;
            }
            this.menu.active_id = id;
            this.menu.show_hidden_list = false;
        },
        toggleHiddenWindowList() {
            if ( !this.hidden_views.length ) {
                return;
            }
            this.menu.show_hidden_list = !this.menu.show_hidden_list;
        },
        /**
         * Restore a hidden view according to the surface type and the invoking pane.
         *
         * Primary reproduction: open any routed list, use its Window Controls ->
         * Show Hidden Views -> Dashboard, and repeat with another primary pane visible.
         * Dashboard must occupy the exact grid rectangle of the pane whose menu was
         * used; it must not append at the far right or displace the other pane.
         *
         * The old implementation showed the target while leaving the source visible,
         * then requested the cell immediately to the source's right. In a two-pane
         * layout that cell was already occupied, so collision handling correctly moved
         * Dashboard again and exposed the actual bug as an unexpected third pane.
         * Dashboard is route-less and owns its own header, so treating it like another
         * generic routed window also puts manager chrome over controls it owns itself.
         *
         * Modal and side-panel views are intentionally different: a modal overlays its
         * primary ancestor, while Assistant and other companions add a separate pane.
         * Keeping those branches here prevents the primary replacement rule from
         * destroying an editor's owner or replacing a list when restoring Assistant.
         */
        openHiddenView( id, source_id = this.active_view_id ) {
            this.closeMenus();

            // A menu can also be invoked from a modal editor or a right-panel record.
            // Those surfaces have temporary/additive geometry, but their primary
            // ancestor owns the durable grid cell. Resolve that ancestor before any
            // replacement so Dashboard never inherits an overlay rectangle and a
            // companion menu never causes an unrelated active pane to be replaced.
            const requested_source_view = source_id ? this.tt_view_stack.getViewById( source_id ) : null;
            const source_view = this.tt_view_stack.getPrimaryAncestorView( requested_source_view );
            const target_view = this.tt_view_stack.getViewById( id );

            // Hidden-menu entries come from live stack state, but the click and a
            // concurrent route transition can still race. Treat a vanished target as
            // an already-resolved request rather than dereferencing stale menu data.
            if ( !target_view ) {
                return;
            }

            // Repro for this exclusion: hide an editor dialog, restore it from its
            // owner's menu, and verify the list remains underneath it. Modal views do
            // not consume grid cells, so hiding the source or applying grid placement
            // here would turn an overlay restore into destructive pane navigation.
            if ( this.isModalWindow( target_view.id ) ) {
                this.tt_view_stack.showView( id );
                return;
            }

            if ( source_view && source_view.id !== target_view.id ) {
                const source_rect = this.getRect( source_view );

                // Repro for the additive branch: open Assistant, open a record, dock
                // the record Right so Assistant becomes hidden, then restore Assistant
                // through Show Hidden Views. The record and Assistant must occupy two
                // distinct companion columns while the primary list remains visible.
                // applyPlacement() deliberately retains collision search here; copying
                // the record's occupied rectangle would stack both companions, while
                // applying the primary replacement rule would wrongly hide the list.
                if ( this.isSidePanelWindow( target_view ) ) {
                    const target_rect = this.getRect( target_view );

                    this.tt_view_stack.showView( id );
                    this.applyPlacement( target_view.id, {
                        x: source_rect.x + source_rect.w,
                        y: source_rect.y,
                        w: target_rect.w,
                        h: target_rect.h,
                    } );

                    return;
                }

                // Show Hidden Views is pane navigation for primary views, not a Dock
                // Right command. Hide the invoking primary first, then copy its exact
                // rectangle so placement sees that cell as available. Reversing those
                // operations recreates the third-pane bug because collision handling
                // quite reasonably refuses to overlap the still-visible source.
                // Preserving the rectangle also retains the user's chosen row/column
                // span and leaves every other visible primary pane untouched.
                source_view.visible = false;
                this.tt_view_stack.showView( id );
                this.applyPlacement( target_view.id, source_rect );

                return;
            }

            // The menu-owning view can disappear between submenu rendering and this
            // click, and defensive/direct callers can supply the target as its own
            // source. In either case there is no distinct pane to replace, so retain
            // the target's saved rectangle and let normal collision policy validate it.
            this.tt_view_stack.showView( id );
            const rect = this.getRect( target_view );
            this.applyPlacement( target_view.id, rect );
        },
        isSidePanelWindow( view ) {
            return this.tt_view_stack.isSidePanelView( view ) === true;
        },
        // Issue #3864: Route-less shell components such as Dashboard and TTAssistantPane own their
        // own chrome. Standalone task modals explicitly opt into these controls;
        // absence of route metadata must never enable them by default.
        // In particular, do not infer that a Dashboard restored into a grid cell has
        // become a routed window. Its top-right area contains application-level controls,
        // and overlaying manager buttons there obscures those controls. Pane replacement
        // is handled by openHiddenView(); changing chrome is neither needed nor correct.
        // A route-less TTModalShell can explicitly set window_controls=true. It then
        // receives these same caption widgets/menu actions as routed Payroll tasks.
        // Do not infer the opt-in from is_modal_view, a label, or a component name:
        // those identify presentation/content, not permission to replace its chrome.
        hasWindowManagerControls( view ) {
            return view?.window_controls === true || ( view?.tt_route_data !== null && view?.tt_route_data !== undefined );
        },
        isPrimaryStackWindow( view ) {
            return !!view && view.visible === true && this.isSidePanelWindow( view ) !== true && this.isModalWindow( view.id ) !== true;
        },
        // A hidden routed workspace remains a valid Pin Right source because pinning
        // must restore it on the left; visible-only primary checks would disable the
        // command immediately after top-level navigation hides the prior workspace.
        isPinRightSourceWindow( view ) {
            return !!view && this.isSidePanelWindow( view ) !== true && this.isModalWindow( view.id ) !== true;
        },
        getPinRightSourceView( view ) {
            if ( !view ) {
                return null;
            }

            const parent_id = this.tt_view_stack.getViewParentId?.( view );
            const parent_view = parent_id ? this.tt_view_stack.getViewById( parent_id ) : null;

            if ( parent_view && parent_view.id !== view.id && this.isPinRightSourceWindow( parent_view ) ) {
                return parent_view;
            }

            const active_view = this.active_view_id ? this.tt_view_stack.getViewById( this.active_view_id ) : null;
            if ( active_view && active_view.id !== view.id && this.isPrimaryStackWindow( active_view ) ) {
                return active_view;
            }

            const visible_source = this.visible_views.find( candidate => candidate.id !== view.id && this.isPrimaryStackWindow( candidate ) );
            if ( visible_source ) {
                return visible_source;
            }

            // Top-level navigation normally hides the previous primary. Falling back
            // to the most recent hidden primary is what lets report criteria opened
            // from the menu become a right companion instead of having no Pin option.
            return [...this.views].reverse().find( candidate => candidate.id !== view.id && this.isPinRightSourceWindow( candidate ) ) ?? null;
        },
        canPinWindowRight( view ) {
            return !!view && this.isSidePanelWindow( view ) !== true && !!this.getPinRightSourceView( view );
        },
        pinWindowRight( id ) {
            const view = this.tt_view_stack.getViewById( id );
            const source_view = this.getPinRightSourceView( view );

            if ( !view || !source_view ) {
                return false;
            }

            this.closeMenus();

            // Manual pinning and routed companions share one store-owned mutation
            // so their split, source identity, replacement, URL, and persistence
            // behavior cannot diverge as the view-stack contract evolves.
            if ( this.tt_view_stack.pinViewRight( id, source_view.id ) !== true ) {
                return false;
            }

            this.clearMismatchedCustomTrackSizes();
            return true;
        },
        popWindowIn( id ) {
            const view = this.tt_view_stack.getViewById( id );

            // Dialogs and docked panels both promote the same mounted editor.
            // Ordinary main views already have this mode and must remain untouched.
            if ( !view || ( this.isModalWindow( id ) !== true && this.isSidePanelWindow( view ) !== true ) ) {
                return false;
            }

            // Snapshot the live companion before maximize clears every peer's pane
            // mode. Promotion makes this view that pane's main/left source;
            // without a companion, the same canonical rectangle fills the workspace.
            const pinned_right_view = this.visible_views.find( candidate => candidate.id !== id && this.isSidePanelWindow( candidate ) );

            this.closeMenus();
            this.tt_view_stack.setViewSurfaceMode( id, {
                is_side_panel: false,
                is_modal_view: false,
            }, {
                sync_url: false,
            } );
            // Maximize owns peer cleanup, but deliberately preserves arbitrary grid
            // geometry. Pop In has a stronger contract: the promoted view always
            // starts at the one-track main slot instead of inheriting its modal,
            // pinned-right, or multi-window footprint.
            this.tt_view_stack.maximizeView( id );
            TTWindowLayoutPolicy.applyRect( view, { x: 0, y: 0, w: 1, h: 1 } );
            this.tt_view_stack.setColSizes( null );
            this.tt_view_stack.setRowSizes( null );

            if ( pinned_right_view ) {
                // Reuse Pin Right's source/slot/route owner so the surviving pane is
                // re-homed to the promoted main view instead of retaining stale source
                // identity or becoming an unrelated hidden grid window.
                this.tt_view_stack.pinViewRight( pinned_right_view.id, id );
            }

            this.clearMismatchedCustomTrackSizes();
            return true;
        },
        popWindowOut( id ) {
            const view = this.tt_view_stack.getViewById( id );

            if ( !view || this.isModalWindow( id ) ) {
                return false;
            }

            this.closeMenus();
            this.tt_view_stack.setViewSurfaceMode( id, {
                is_side_panel: false,
                is_modal_view: true,
            } );
            this.clearMismatchedCustomTrackSizes();
            return true;
        },
        getViewLabel( view ) {
            // Shell schema labels identify owner-local tasks; route module/action is
            // only the safe fallback while metadata is absent or failed to load.
            const shell_label = this.shell_layouts?.[view?.id]?.label;
            if ( typeof shell_label === 'string' && shell_label.length > 0 ) {
                return shell_label;
            }

            // Loaded record identity names this window, its controls and its
            // minimized tab through the same derived context.
            // This is per-window presentation, so it can reflect the displayed
            // record while navigation is still loading. Only the authenticated
            // shell waits for URL commitment before writing browser history.
            return this.tt_view_stack.getViewScreenContext( view )?.title || this.labels.default_view;
        },
        getViewModule( view ) {
            return typeof view?.tt_route_data?.getModule === 'function' ? view.tt_route_data.getModule() : this.labels.default_view;
        },
        getViewAction( view ) {
            return typeof view?.tt_route_data?.getAction === 'function' ? view.tt_route_data.getAction() : '';
        },
        getViewScreen( view ) {
            const module = this.getViewModule( view );
            const action = this.getViewAction( view );

            return action ? `${module}.${action}` : module;
        },
        getWindowRole( view ) {
            return this.isWindowActiveModal( view ) ? 'dialog' : null;
        },
        isWindowActiveModal( view ) {
            return view?.visible === true && this.active_modal_view_id === view.id;
        },
        isWindowExclusiveActiveModal( view ) {
            // A feature modal may deliberately keep a companion Assistant pane usable.
            // In that layout the modal still covers its primary parent, but aria-modal
            // must not claim that every outside surface (including the pane) is inert.
            return this.isWindowActiveModal( view )
                && this.has_active_modal_side_panel === false;
        },
        getWindowSourcePane( view ) {
            // Geometry deliberately differs from modal companion/ARIA ancestry: a
            // docked editor is itself a source pane, not its primary list ancestor.
            // Other modal types and single-pane layouts retain established bounds.
            if ( !view || !this.isModalWindow( view.id ) || !this.requiresEditLaneSizing( view.id ) || this.getOccupiedLayoutViews().length < 2 ) {
                return null;
            }

            // Parent links are mutable route state, so malformed cycles must fail
            // closed instead of blocking rendering or selecting arbitrary bounds.
            const visited_view_ids = new Set();
            while ( view && this.isModalWindow( view.id ) ) {
                if ( visited_view_ids.has( view.id ) ) {
                    return null;
                }
                visited_view_ids.add( view.id );
                view = this.tt_view_stack.getViewById( this.tt_view_stack.getViewParentId( view ) );
            }

            // Hidden/missing launchers cannot supply usable bounds. Leave the
            // preference intact so restoring its pane restores confinement too.
            return view?.visible === true ? view : null;
        },
        isWindowActiveModalCompanion( view ) {
            // Retained hidden panes never weaken the active modal's coverage.
            if ( !this.active_modal_view_id || view?.visible !== true ) {
                return false;
            }

            // Compare durable primary owners rather than immediate parents because the requesting
            // feature may itself be nested under an edit modal. Matching immediate modal IDs would
            // fail for Formula Builder -> Assistant and could match malformed overlay chains.
            const active_modal = this.tt_view_stack.getViewById( this.active_modal_view_id );
            const modal_primary = this.tt_view_stack.getPrimaryAncestorView( active_modal );
            if ( !modal_primary ) {
                return false;
            }

            if ( this.isSidePanelWindow( view ) === true ) {
                if ( modal_primary.id === this.tt_view_stack.getPrimaryAncestorView( view )?.id ) {
                    return true;
                }

                // Issue #3749: Jump To TimeSheet pins the request under TimeSheet, then
                // reopening Request Authorizations hides TimeSheet but keeps its right pane
                // visible. A dialog confined to the new left list must not make that
                // retained pane inert or remove its Controls and Close. An unconfined
                // modal, visible unrelated owner, or broken parent chain stays covered.
                return this.tt_view_stack.getPrimaryAncestorView( view )?.visible === false
                    && this.float_within_pane[active_modal.id] !== false
                    && this.getWindowSourcePane( active_modal )?.id === modal_primary.id;
            }

            // Issue #3741: when a modal is launched from a docked pane, its primary ancestor is
            // the other half of the same composite surface. Treat it symmetrically with the pane;
            // otherwise docking before opening a record makes the primary inert while opening the
            // record before docking leaves it interactive. Geometry remains independently governed
            // by Float Within Pane, so this relationship is stable in either display mode.
            return view.id === modal_primary.id && this.isSidePanelWindow( this.getWindowSourcePane( active_modal ) ) === true;
        },
        isWindowCoveredByActiveModal( view ) {
            // Issue #3749: A related companion surface, or a retained pane
            // beside a confined modal, remains interactive. Other surfaces stay
            // covered with their controls hidden and their content inert.
            return !!this.active_modal_view_id
                && view?.id !== this.active_modal_view_id
                && this.isWindowActiveModalCompanion( view ) !== true;
        },
        isWindowRendered( view ) {
            if ( view?.visible !== true ) {
                return false;
            }

            if ( this.isModalWindow( view.id ) ) {
                return view.id === this.active_view_id;
            }

            return true;
        },
        isWindowAutomationActive( view ) {
            return view?.visible === true && view.id === this.active_view_id && !this.isWindowAutomationHidden( view );
        },
        isWindowAutomationHidden( view ) {
            return this.isWindowRendered( view ) !== true || this.isWindowCoveredByActiveModal( view );
        },

        setActiveWindow( id ) {
            const view = this.tt_view_stack.getViewById( id );
            // Issue #3741: the related primary is interactive but the dock-launched modal still
            // owns the active stack slot. Promoting the primary on mousedown would make
            // TTViewStack hide the unsaved modal before the clicked primary control can run.
            if ( this.isSidePanelWindow( view ) !== true && this.isWindowActiveModalCompanion( view ) === true ) {
                return;
            }

            this.tt_view_stack.setActiveView( id );
        },
        getGridSplitterLabel( axis, boundary_index ) {
            const label = axis === 'column' ? this.labels.resize_columns : this.labels.resize_rows;

            return label.replace( '%1', boundary_index + 1 ).replace( '%2', boundary_index + 2 );
        },
        gridSplitterStyle( axis, splitter ) {
            return axis === 'column'
                ? {
                    '--splitter-x': `${splitter.position}%`,
                    '--splitter-cross-start': `${splitter.cross_start}%`,
                    '--splitter-cross-size': `${splitter.cross_end - splitter.cross_start}%`,
                }
                : {
                    '--splitter-y': `${splitter.position}%`,
                    '--splitter-cross-start': `${splitter.cross_start}%`,
                    '--splitter-cross-size': `${splitter.cross_end - splitter.cross_start}%`,
                };
        },
        getGridSplitterSegments( axis ) {
            const track_count = this.getGridResizeTrackCount( axis );
            const cross_count = axis === 'column' ? this.grid_row_count : this.grid_col_count;
            const boundaries = TTWindowLayoutPolicy.getTrackBoundaries( this.getGridResizeTrackSizes( axis ), track_count );
            const cross_sizes = TTWindowLayoutPolicy.getNormalizedTrackSizes(
                axis === 'column' ? this.tt_view_stack.layout_grid?.row_sizes : this.tt_view_stack.layout_grid?.col_sizes,
                cross_count
            );
            // Issue #3864: Apply modal eligibility before discovering shared edges.
            // Using every occupied pane here and hiding the result only in the
            // template would leave pointer/keyboard entry points with a broader
            // resize contract than the rendered separator. All three paths ask
            // this method for the same current set of permitted boundaries.
            const occupied_views = this.grid_resize_views;
            const segments = [];
            const last_segment_by_boundary = new Map();
            let cross_start = 0;

            // A grid track boundary is global, but its hit target belongs only where
            // two distinct panes actually meet. Omitting spanning/empty cells prevents
            // an invisible 11px strip from stealing clicks inside one large window.
            for ( let cross_index = 0; cross_index < cross_count; cross_index++ ) {
                const cross_end = cross_start + cross_sizes[cross_index];

                boundaries.forEach( boundary => {
                    const before_edge = boundary.index + 1;
                    const has_before_view = occupied_views.some( view => {
                        const rect = this.getRect( view );

                        return axis === 'column'
                            ? rect.y <= cross_index && rect.y + rect.h > cross_index && rect.x + rect.w === before_edge
                            : rect.x <= cross_index && rect.x + rect.w > cross_index && rect.y + rect.h === before_edge;
                    } );
                    const has_after_view = occupied_views.some( view => {
                        const rect = this.getRect( view );

                        return axis === 'column'
                            ? rect.y <= cross_index && rect.y + rect.h > cross_index && rect.x === before_edge
                            : rect.x <= cross_index && rect.x + rect.w > cross_index && rect.y === before_edge;
                    } );

                    if ( has_before_view && has_after_view ) {
                        const last_segment = last_segment_by_boundary.get( boundary.index );

                        if ( last_segment && Math.abs( last_segment.cross_end - cross_start ) < 0.0001 ) {
                            last_segment.cross_end = cross_end;
                        } else {
                            const next_segment = {
                                ...boundary,
                                segment_index: cross_index,
                                cross_start: cross_start,
                                cross_end: cross_end,
                            };

                            segments.push( next_segment );
                            last_segment_by_boundary.set( boundary.index, next_segment );
                        }
                    }
                } );

                cross_start = cross_end;
            }

            return segments;
        },
        isGridSplitterActive( axis, boundary_index ) {
            return this.grid_resize.axis === axis && this.grid_resize.boundary_index === boundary_index;
        },
        getGridResizeTrackCount( axis ) {
            return axis === 'column' ? this.grid_col_count : this.grid_row_count;
        },
        getGridResizeTrackSizes( axis ) {
            return axis === 'column'
                ? this.tt_view_stack.layout_grid?.col_sizes
                : this.tt_view_stack.layout_grid?.row_sizes;
        },
        applyGridResize( axis, boundary_index, start_sizes, delta_pixels, total_pixels ) {
            const next_sizes = TTWindowLayoutPolicy.resizeTrackBoundary(
                start_sizes,
                this.getGridResizeTrackCount( axis ),
                boundary_index,
                delta_pixels,
                total_pixels,
                // 200px keeps list/chat panes usable; rows can remain shorter because
                // their own scroll containers preserve controls and content access.
                axis === 'column' ? 200 : 120
            );

            if ( !next_sizes ) {
                return false;
            }

            if ( axis === 'column' ) {
                this.tt_view_stack.setColSizes( next_sizes );
            } else {
                this.tt_view_stack.setRowSizes( next_sizes );
            }

            return true;
        },
        startGridResize( axis, boundary_index, event ) {
            // Issue #3864: Rendering, pointer and keyboard share boundary eligibility;
            // checking only modal presence would still reject the allowed companion divider.
            // Recompute at gesture start: a nested modal or pane replacement may
            // have changed eligibility since the separator was last rendered.
            // Keep non-primary mouse buttons from starting pointer capture as before.
            if ( !this.getGridSplitterSegments( axis ).some( segment => segment.index === boundary_index )
                || ( event.pointerType === 'mouse' && event.button !== 0 ) ) {
                return;
            }

            const grid_rect = this.$refs.layout_grid?.getBoundingClientRect?.();
            const total_pixels = axis === 'column' ? grid_rect?.width : grid_rect?.height;
            if ( !total_pixels ) {
                return;
            }

            // The authenticated shell owns the viewport boundary. Keep splitter focus
            // from moving any pane-local scroll owner when pointer resizing begins.
            event.currentTarget?.focus?.( { preventScroll: true } );
            event.preventDefault();
            event.stopPropagation();
            this.closeMenus();
            this.grid_resize.axis = axis;
            this.grid_resize.boundary_index = boundary_index;
            this.grid_resize.pointer_id = event.pointerId;
            this.grid_resize.start_coordinate = axis === 'column' ? event.clientX : event.clientY;
            // Snapshot normalized percentages at gesture start. Recomputing from each
            // move compounds rounding and makes the divider drift behind the pointer.
            this.grid_resize.start_sizes = TTWindowLayoutPolicy.getNormalizedTrackSizes( this.getGridResizeTrackSizes( axis ), this.getGridResizeTrackCount( axis ) );
            this.grid_resize.total_pixels = total_pixels;
            event.currentTarget?.setPointerCapture?.( event.pointerId );
        },
        onGridResizePointerMove( axis, boundary_index, event ) {
            if ( !this.isGridSplitterActive( axis, boundary_index ) || this.grid_resize.pointer_id !== event.pointerId ) {
                return;
            }

            event.preventDefault();
            this.applyGridResize(
                axis,
                boundary_index,
                this.grid_resize.start_sizes,
                ( axis === 'column' ? event.clientX : event.clientY ) - this.grid_resize.start_coordinate,
                this.grid_resize.total_pixels
            );
        },
        finishGridResize( event ) {
            if ( this.grid_resize.pointer_id === null || ( event?.pointerId !== undefined && event.pointerId !== this.grid_resize.pointer_id ) ) {
                return;
            }

            const pointer_id = this.grid_resize.pointer_id;
            this.grid_resize.axis = null;
            this.grid_resize.boundary_index = null;
            this.grid_resize.pointer_id = null;
            this.grid_resize.start_sizes = null;
            this.grid_resize.total_pixels = 0;

            // Clear reactive gesture state before releasing capture because browsers
            // may synchronously emit lostpointercapture from releasePointerCapture().
            try {
                if ( event?.currentTarget?.hasPointerCapture?.( pointer_id ) ) {
                    event.currentTarget.releasePointerCapture( pointer_id );
                }
            } catch {
                // Capture may already be gone after pointercancel; state cleanup above
                // is authoritative and must not be rolled back by that browser race.
            }

            // Grid resizing changes component width without changing viewport width.
            // Dispatch once at gesture completion for legacy children that still size
            // themselves from the global resize contract.
            window.dispatchEvent( new Event( 'resize' ) );
        },
        onGridResizeKeyDown( axis, boundary_index, event ) {
            const key_direction = axis === 'column'
                ? { ArrowLeft: -1, ArrowRight: 1 }
                : { ArrowUp: -1, ArrowDown: 1 };
            // Issue #3864: Apply the same modal/companion boundary guard to keyboard
            // resizing. A stale/direct key event must not bypass a separator that
            // disappeared when a modal became exclusive or its owner was removed.
            // Native separator focus stays intact; this does not activate another
            // window or change the editor's active/modal lifetime.
            if ( !key_direction[event.key] || !this.getGridSplitterSegments( axis ).some( segment => segment.index === boundary_index ) ) {
                return;
            }

            const grid_rect = this.$refs.layout_grid?.getBoundingClientRect?.();
            const total_pixels = axis === 'column' ? grid_rect?.width : grid_rect?.height;
            if ( !total_pixels ) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            this.applyGridResize(
                axis,
                boundary_index,
                this.getGridResizeTrackSizes( axis ),
                key_direction[event.key] * ( event.shiftKey ? 40 : 10 ),
                total_pixels
            );
            window.dispatchEvent( new Event( 'resize' ) );
        },
        canMinimizeWindow( view ) {
            return !!view && this.isSidePanelWindow( view ) !== true && this.isWizardWindow( view.id ) !== true && this.tt_view_stack.canMinimizeView( view.id ) === true;
        },
        /**
         * Minimize a caption-controlled shell.
         *
         * TTInteractionManager has already captured and scoped the activation to this
         * view. Keeping the command parameterless means captions, Wizards, and future
         * shortcut callers all share one correlation path. closeMenus() stays first so a
         * Controls overlay cannot remain orphaned over the fallback view after collapse.
         */
        minimizeWindow( id ) {
            this.closeMenus();
            return this.tt_view_stack.minimizeView( id );
        },
        closeWindow( id ) {
            this.closeMenus();
            if ( this.isWindowCloseBlocked( id ) ) {
                return false;
            }

            // Caption Close must share the menu's guarded removal path so dirty
            // mounted editors still confirm before their state is discarded. Repro the
            // reflow contract with left/middle/right panes and close the middle caption:
            // after confirmation the right survivor must shift left; after rejection no
            // pane may move. The view_ids watcher observes only a successful removal,
            // which keeps geometry unchanged while a dirty-close prompt is rejected.
            return this.tt_view_stack.requestRemoveView( id, true );
        },
        syncWindowStateCaches() {
            const live_ids = new Set( this.views.map( view => view.id ) );

            Object.keys( this.window_render_signatures ).forEach( id => {
                if ( live_ids.has( id ) !== true ) {
                    delete this.window_render_signatures[id];
                    this.clearWindowSizingState( id );
                }
            } );

            this.views.forEach( view => {
                const render_signature = this.getWindowRenderKey( view );

                if ( this.window_render_signatures[view.id] && this.window_render_signatures[view.id] !== render_signature ) {
                    this.clearWindowSizingState( view.id );
                }

                if ( this.float_within_pane[view.id] === undefined ) {
                    // Stack parents precede their children. Snapshot inheritance
                    // once; later parent toggles must not change an open child.
                    this.float_within_pane[view.id] = this.float_within_pane[this.tt_view_stack.getViewParentId( view )] !== false;
                }
                this.window_render_signatures[view.id] = render_signature;
            } );
        },
        clearWindowSizingState( id ) {
            // A removed/remounted editor gets a fresh preference; record/tab changes
            // that preserve its render identity keep the current choice.
            delete this.float_within_pane[id];
            delete this.edit_lane_counts[id];
            delete this.locked_edit_lane_counts[id];
            delete this.edit_shell_heights[id];
            delete this.locked_edit_shell_heights[id];
            delete this.edit_shell_heights_by_mode[id];
            delete this.locked_edit_shell_heights_by_mode[id];
            delete this.edit_shell_widths[id];
            delete this.locked_edit_shell_widths[id];
            delete this.wizard_shell_heights[id];
            delete this.wizard_shell_widths[id];
            delete this.content_sized_wizard_shells[id];
            delete this.content_sized_edit_shells[id];
            // Shell presentation and close vetoes are scoped to one route identity;
            // retaining either would contaminate a later view that reuses the ID.
            delete this.shell_layouts[id];
            delete this.window_close_blocked[id];
        },

        setShellLayout( id, layout = {} ) {
            // Only shell routes may populate these presentation tokens. This keeps
            // owner metadata from overriding calibrated edit or Wizard dimensions.
            if ( !id || this.getWindowAction( id ) !== 'shell' ) {
                return false;
            }

            this.shell_layouts[id] = {
                label: typeof layout?.label === 'string' ? layout.label : null,
                width: this.normalizeEditShellWidth( layout.width ),
                height: this.normalizeEditShellHeight( layout.height ),
            };
            return true;
        },
        setWindowCloseBlocked( id, is_blocked ) {
            // The veto is deliberately transient: it covers owner processing and is
            // deleted on completion so caption Close and Escape share one decision.
            if ( !id || this.getWindowAction( id ) !== 'shell' ) {
                return false;
            }

            if ( is_blocked === true ) {
                this.window_close_blocked[id] = true;
            } else {
                delete this.window_close_blocked[id];
            }

            return true;
        },
        isWindowCloseBlocked( id ) {
            return this.window_close_blocked[id] === true;
        },
        setWizardShellLayout( id, layout = {} ) {
            if ( !id ) {
                return;
            }

            this.setWizardShellHeight( id, layout?.shell_height );
            this.setWizardShellWidth( id, layout?.shell_width );
            // Explicit heights and intrinsic sizing are competing contracts. A declared height wins so a future metadata merge cannot make a calibrated canvas resize with its contents.
            if ( layout?.content_sized_shell === true && !this.getWindowWizardShellHeight( id ) ) {
                this.content_sized_wizard_shells[id] = true;
            } else {
                delete this.content_sized_wizard_shells[id];
            }
        },
        setWizardShellHeight( id, shell_height ) {
            if ( !id ) {
                return;
            }

            const normalized_height = this.normalizeEditShellHeight( shell_height );
            if ( normalized_height ) {
                this.wizard_shell_heights[id] = normalized_height;
            } else {
                delete this.wizard_shell_heights[id];
            }
        },
        setWizardShellWidth( id, shell_width ) {
            if ( !id ) {
                return;
            }

            const normalized_width = this.normalizeEditShellWidth( shell_width );
            if ( normalized_width ) {
                this.wizard_shell_widths[id] = normalized_width;
            } else {
                delete this.wizard_shell_widths[id];
            }
        },

        onEditLaneCount( id, event ) {
            const lane_count = event?.detail?.lane_count;
            const source_id = event?.detail?.source_id || 'default';
            const lock_lane_count = event?.detail?.lock_lane_count === true;
            const shell_height = event?.detail?.shell_height;
            const shell_heights_by_mode = event?.detail?.shell_heights_by_mode;
            const shell_width = event?.detail?.shell_width;
            const content_sized_shell = event?.detail?.content_sized_shell;

            this.setEditLaneCount( id, lane_count, source_id, { lock: lock_lane_count, shell_height, shell_heights_by_mode, shell_width, content_sized_shell } );
        },
        setEditLaneCount( id, lane_count, source_id = 'default', options = {} ) {
            if ( !id ) {
                return;
            }

            this.syncWindowStateCaches();
            if ( this.locked_edit_lane_counts[id] ) {
                return;
            }

            if ( lane_count === 1 || lane_count === 2 || lane_count === 3 ) {
                if ( !this.edit_lane_counts[id] || typeof this.edit_lane_counts[id] !== 'object' ) {
                    this.edit_lane_counts[id] = {};
                }

                this.edit_lane_counts[id][source_id] = lane_count;
                const shell_height = this.normalizeEditShellHeight( options.shell_height );
                const shell_heights_by_mode = this.normalizeEditShellHeightsByMode( options.shell_heights_by_mode );
                const shell_width = this.normalizeEditShellWidth( options.shell_width );

                // Only an explicit boolean may change the sizing capability. Live
                // EditRecords lane events omit this option and must not erase the
                // loader's prepaint decision while the same window is mounted.
                if ( options.content_sized_shell === true ) {
                    this.content_sized_edit_shells[id] = true;
                } else if ( options.content_sized_shell === false ) {
                    delete this.content_sized_edit_shells[id];
                }

                if ( shell_height ) {
                    if ( !this.edit_shell_heights[id] || typeof this.edit_shell_heights[id] !== 'object' ) {
                        this.edit_shell_heights[id] = {};
                    }

                    this.edit_shell_heights[id][source_id] = shell_height;
                }

                if ( shell_heights_by_mode ) {
                    if ( !this.edit_shell_heights_by_mode[id] || typeof this.edit_shell_heights_by_mode[id] !== 'object' ) {
                        this.edit_shell_heights_by_mode[id] = {};
                    }

                    this.edit_shell_heights_by_mode[id][source_id] = shell_heights_by_mode;
                }

                if ( shell_width ) {
                    if ( !this.edit_shell_widths[id] || typeof this.edit_shell_widths[id] !== 'object' ) {
                        this.edit_shell_widths[id] = {};
                    }

                    this.edit_shell_widths[id][source_id] = shell_width;
                }

                if ( options.lock !== false ) {
                    this.locked_edit_lane_counts[id] = lane_count;

                    if ( shell_height ) {
                        this.locked_edit_shell_heights[id] = shell_height;
                    }

                    if ( shell_heights_by_mode ) {
                        this.locked_edit_shell_heights_by_mode[id] = shell_heights_by_mode;
                    }

                    if ( shell_width ) {
                        this.locked_edit_shell_widths[id] = shell_width;
                    }
                }

                return;
            }

            if ( this.edit_lane_counts[id] && typeof this.edit_lane_counts[id] === 'object' ) {
                delete this.edit_lane_counts[id][source_id];

                if ( Object.keys( this.edit_lane_counts[id] ).length === 0 ) {
                    delete this.edit_lane_counts[id];
                }
            }

            if ( this.edit_shell_heights[id] && typeof this.edit_shell_heights[id] === 'object' ) {
                delete this.edit_shell_heights[id][source_id];

                if ( Object.keys( this.edit_shell_heights[id] ).length === 0 ) {
                    delete this.edit_shell_heights[id];
                }
            }

            if ( this.edit_shell_heights_by_mode[id] && typeof this.edit_shell_heights_by_mode[id] === 'object' ) {
                delete this.edit_shell_heights_by_mode[id][source_id];

                if ( Object.keys( this.edit_shell_heights_by_mode[id] ).length === 0 ) {
                    delete this.edit_shell_heights_by_mode[id];
                }
            }

            if ( this.edit_shell_widths[id] && typeof this.edit_shell_widths[id] === 'object' ) {
                delete this.edit_shell_widths[id][source_id];

                if ( Object.keys( this.edit_shell_widths[id] ).length === 0 ) {
                    delete this.edit_shell_widths[id];
                }
            }
        },
        normalizeEditShellWidth( shell_width ) {
            if ( typeof shell_width === 'number' && Number.isFinite( shell_width ) && shell_width > 0 ) {
                return `${Math.round( shell_width * 100 ) / 100}rem`;
            }

            if ( typeof shell_width !== 'string' ) {
                return null;
            }

            const normalized_width = shell_width.trim();
            if ( /^\d+(\.\d+)?(rem|px|%)$/.test( normalized_width ) ) {
                return normalized_width;
            }

            return null;
        },
        normalizeEditShellHeight( shell_height ) {
            if ( typeof shell_height === 'number' && Number.isFinite( shell_height ) && shell_height > 0 ) {
                return `${Math.round( shell_height * 100 ) / 100}rem`;
            }

            if ( typeof shell_height !== 'string' ) {
                return null;
            }

            const normalized_height = shell_height.trim();
            if ( /^\d+(\.\d+)?(rem|px|%)$/.test( normalized_height ) ) {
                return normalized_height;
            }

            return null;
        },
        normalizeEditShellHeightsByMode( shell_heights_by_mode ) {
            if ( !shell_heights_by_mode || typeof shell_heights_by_mode !== 'object' || Array.isArray( shell_heights_by_mode ) ) {
                return null;
            }

            const allowed_modes = ['lanes_3', 'lanes_2', 'lanes_1', 'lanes_1_stacked'];
            const normalized_heights = {};

            allowed_modes.forEach( mode => {
                const normalized_height = this.normalizeEditShellHeight( shell_heights_by_mode[mode] );
                if ( normalized_height ) {
                    normalized_heights[mode] = normalized_height;
                }
            } );

            return Object.keys( normalized_heights ).length > 0 ? normalized_heights : null;
        },
        // Return the larger of the current locked edit-shell height and a tab's
        // requested minimum, but only when the two values are directly comparable.
        // This helper deliberately does not measure DOM pixels: shell locks are
        // stored as declarative CSS units, and converting percent/rem/px in JS would
        // make the result viewport- and font-size-dependent in a place that should
        // remain stable across render cycles.
        getLargerEditShellHeight( current_height, minimum_height ) {
            const normalized_current_height = this.normalizeEditShellHeight( current_height );
            const normalized_minimum_height = this.normalizeEditShellHeight( minimum_height );

            // Invalid tab metadata should be ignored rather than clearing an
            // existing shell lock. A bad tab value must fail closed to "leave the
            // editor exactly as it was" because this manager is shared by every
            // modal edit surface.
            if ( !normalized_minimum_height ) {
                return normalized_current_height;
            }

            // A modal with no existing lock can adopt the tab minimum directly.
            // This covers future callers that request a minimum before TTViewLoader
            // has computed the usual lane-based height.
            if ( !normalized_current_height ) {
                return normalized_minimum_height;
            }

            const current_match = normalized_current_height.match( /^(\d+(?:\.\d+)?)(rem|px|%)$/ );
            const minimum_match = normalized_minimum_height.match( /^(\d+(?:\.\d+)?)(rem|px|%)$/ );

            // Percent heights are viewport-relative configured shells. Do not replace
            // them with a tab minimum in rem/px because that would shrink full-height
            // editors such as Pay Stub or GEO Fence when Audit is selected.
            //
            // Mixed units also cannot be safely ordered here without a live DOM
            // measurement and breakpoint knowledge. Keeping the current value is the
            // non-destructive choice: it preserves existing configured shells and
            // still lets same-unit short editors grow.
            if ( !current_match || !minimum_match || current_match[2] !== minimum_match[2] ) {
                return normalized_current_height;
            }

            // Same-unit numeric comparison is intentionally the only place this
            // helper changes height. That keeps the method a pure "raise the floor"
            // operation and protects the no-shrink behavior requested for tab
            // switching.
            return Number( current_match[1] ) >= Number( minimum_match[1] )
                ? normalized_current_height
                : normalized_minimum_height;
        },
        // Apply a tab-supplied minimum to the locked edit shell height for a view.
        //
        // This is a tab-activation path, not an initial layout-estimation path:
        // TTViewLoader intentionally excludes subview tabs such as Audit from the
        // first-paint field-height calculation so compact editors open compactly.
        // Once the user selects a lazy table tab, this method raises the shell just
        // enough for that table and never lowers it again for the lifetime of the
        // edit window.
        setEditShellMinHeight( id, shell_min_height ) {
            // Without a view-stack id there is no safe target for the shell lock.
            // Failing quietly keeps embedded/UI Kit renderers from accidentally
            // applying tab metadata to the active window or to global CSS.
            if ( !id ) {
                return;
            }

            const normalized_shell_min_height = this.normalizeEditShellHeight( shell_min_height );
            // Treat malformed schema metadata as absent. The tab still loads; only
            // the optional sizing hint is ignored.
            if ( !normalized_shell_min_height ) {
                return;
            }

            // Pull any pending lane-count/height estimates into the lock caches
            // before comparing. Otherwise a tab click that lands between initial TTS
            // sizing and render could be overwritten by stale cache state.
            this.syncWindowStateCaches();

            // The generic locked height is used directly when no responsive
            // per-mode height exists, and as the fallback in the CSS variable chain
            // when a mode-specific value is missing.
            const current_locked_height = this.locked_edit_shell_heights[id] || this.getWindowEditShellHeight( id );
            const next_locked_height = this.getLargerEditShellHeight( current_locked_height, normalized_shell_min_height );
            if ( next_locked_height && next_locked_height !== current_locked_height ) {
                this.locked_edit_shell_heights[id] = next_locked_height;
            }

            // TTViewLoader often provides calibrated heights per lane mode. Those
            // variables override the generic lock in CSS, so raising only
            // --modal-shell-locked-height would not help the current mode on short
            // one-lane editors like Employee Title.
            const current_mode_heights = this.locked_edit_shell_heights_by_mode[id] || this.getWindowEditShellHeightsByMode( id );
            if ( current_mode_heights ) {
                const next_mode_heights = {};
                // Responsive mode heights override the generic locked height in CSS,
                // so every known mode must be raised or one-lane Audit tabs can stay
                // cramped even after --modal-shell-locked-height grows.
                Object.entries( current_mode_heights ).forEach( ( [mode, height] ) => {
                    next_mode_heights[mode] = this.getLargerEditShellHeight( height, normalized_shell_min_height );
                } );
                this.locked_edit_shell_heights_by_mode[id] = next_mode_heights;
            }
        },

        handleWindowMenuAction( action, id ) {
            this.closeMenus();

            switch ( action ) {
                case 'popIn':
                    this.popWindowIn( id );
                    break;
                case 'popOut':
                    this.popWindowOut( id );
                    break;
                case 'pinRight':
                    this.pinWindowRight( id );
                    break;
                case 'toggleFloatWithinPane':
                    // Recheck eligibility because a source pane can disappear after
                    // its menu opens. Only presentation state changes, never the route.
                    if ( this.getWindowSourcePane( this.tt_view_stack.getViewById( id ) ) ) {
                        this.float_within_pane[id] = this.float_within_pane[id] === false;
                    }
                    this.$nextTick( () => {
                        // The checkbox is removed with its menu. Restore the stable
                        // caption trigger without stealing focus from a newer modal.
                        if ( id === this.active_modal_view_id ) {
                            Array.from( this.$el?.querySelectorAll?.( '[data-tt-action="window-menu"]' ) || [] )
                                .find( element => element.getAttribute( 'data-tt-view-id' ) === String( id ) && element.closest( '[inert]' ) === null )?.focus();
                        }
                    } );
                    break;
                case 'toggleAutoPin':
                    // Request persistence from the authenticated parent instead of
                    // changing this session alone; failed saves retain the prior choice.
                    this.$emit( 'save-open-records-in', this.tt_view_stack.auto_pin_enabled ? 'dialog' : 'right_panel' );
                    break;
                case 'maximize':
                    this.maximizeWindow( id );
                    break;
                case 'toggleAutoFill':
                    this.toggleAutoFill();
                    break;
                case 'toggleVisibility':
                    this.tt_view_stack.toggleViewVisibility( id );
                    this.applyAutoFillIfEnabled();
                    break;
                case 'close':
                    // Window-menu Close is a user discard path, unlike router-owned
                    // stack cleanup. It shares the same three-pane reproduction and
                    // dirty-confirmation invariant as caption Close above. Reflow stays
                    // watcher-owned so this entry point cannot drift from minimized-tab,
                    // Cancel, or router removals as new close affordances are added.
                    this.tt_view_stack.requestRemoveView( id, true );
                    break;

                case 'moveLeft':
                    this.moveBy( id, -1, 0 );
                    break;
                case 'moveRight':
                    this.moveBy( id, 1, 0 );
                    break;
                case 'moveUp':
                    this.moveBy( id, 0, -1 );
                    break;
                case 'moveDown':
                    this.moveBy( id, 0, 1 );
                    break;

                case 'increaseColSpan':
                    this.resizeBy( id, 1, 0 );
                    break;
                case 'decreaseColSpan':
                    this.resizeBy( id, -1, 0 );
                    break;
                case 'increaseRowSpan':
                    this.resizeBy( id, 0, 1 );
                    break;
                case 'decreaseRowSpan':
                    this.resizeBy( id, 0, -1 );
                    break;

                default:
                    break;
            }
        },
        rectOverlaps( a, b ) {
            return TTWindowLayoutPolicy.rectOverlaps( a, b );
        },
        getRect( view ) {
            return TTWindowLayoutPolicy.getViewRect( view );
        },
        getGridBounds() {
            return this.getManagedLayoutViews().reduce( ( bounds, view ) => {
                const rect = this.getRect( view );

                return {
                    cols: Math.max( bounds.cols, rect.x + rect.w ),
                    rows: Math.max( bounds.rows, rect.y + rect.h ),
                };
            }, { cols: 1, rows: 1 } );
        },
        isRectFree( rect, excludeId ) {
            return TTWindowLayoutPolicy.isRectFree( rect, this.getOccupiedLayoutViews(), excludeId );
        },
        findNearestFree( rect, excludeId ) {
            return TTWindowLayoutPolicy.findNearestFree( rect, this.getOccupiedLayoutViews(), excludeId, this.grid_col_count, this.grid_row_count );
        },

        applyPlacement( id, proposedRect ) {
            const view = this.tt_view_stack.getViewById( id );
            if ( !view ) return;

            const rect = TTWindowLayoutPolicy.normalizeRect( proposedRect );
            const finalRect = this.isRectFree( rect, id ) ? rect : this.findNearestFree( rect, id );

            TTWindowLayoutPolicy.applyRect( view, finalRect );
            this.applyAutoFillIfEnabled();
            this.clearMismatchedCustomTrackSizes();
        },
        maximizeWindow( id ) {
            const view = this.tt_view_stack.getViewById( id );
            if ( !view || this.tt_view_stack.isSidePanelView( view ) ) {
                return;
            }

            const bounds = this.getGridBounds();

            TTWindowLayoutPolicy.applyRect( view, { x: 0, y: 0, w: bounds.cols, h: bounds.rows } );
            this.tt_view_stack.maximizeView( id );
        },
        toggleAutoFill() {
            this.auto_fill_enabled = this.auto_fill_enabled !== true;

            if ( this.auto_fill_enabled === true ) {
                this.fillWindowRowGaps();
            }
        },
        applyAutoFillIfEnabled() {
            if ( this.auto_fill_enabled === true ) {
                this.fillWindowRowGaps();
            }
        },
        isManagedLayoutView( view ) {
            return view?.visible === true && this.tt_view_stack.isSidePanelView( view ) !== true && this.isModalWindow( view.id ) !== true;
        },
        getManagedLayoutViews() {
            return this.visible_views.filter( view => this.isManagedLayoutView( view ) );
        },
        isOccupiedLayoutView( view ) {
            return view?.visible === true && this.isModalWindow( view.id ) !== true;
        },
        getOccupiedLayoutViews() {
            return this.visible_views.filter( view => this.isOccupiedLayoutView( view ) );
        },
        fillWindowRowGaps() {
            if ( this.visible_views.some( view => this.tt_view_stack.isSidePanelView( view ) ) ) {
                // Right-pinned panels occupy a fixed companion column. Auto Fill is
                // skipped while one is visible so normal windows do not expand into
                // the reserved right pane and cover the pinned view.
                return;
            }

            const compactable_views = this.getManagedLayoutViews();
            if ( compactable_views.length < 1 ) {
                return;
            }

            // Exact gap reproduction: arrange a tall window in column 0, close the
            // window in column 1, and leave a shorter window in column 2. Row filling
            // groups windows by identical vertical span, so it cannot safely expand
            // either survivor across the blank shared track. Collapse columns first to
            // move column 2 to column 1, giving the later row compaction/filling passes
            // normalized coordinates. The essential invariant is that whole-column
            // compaction runs; keeping it first makes each later pass consume that result.
            const collapsed_column_rects = TTWindowLayoutPolicy.collapseEmptyColumns( compactable_views );

            compactable_views.forEach( view => {
                const collapsed_rect = collapsed_column_rects[view.id];
                if ( collapsed_rect ) {
                    TTWindowLayoutPolicy.applyRect( view, collapsed_rect );
                }
            } );

            const collapsed_row_rects = TTWindowLayoutPolicy.collapseEmptyRows( compactable_views );

            compactable_views.forEach( view => {
                const collapsed_rect = collapsed_row_rects[view.id];
                if ( collapsed_rect ) {
                    TTWindowLayoutPolicy.applyRect( view, collapsed_rect );
                }
            } );

            const filled_rects = TTWindowLayoutPolicy.fillRowGaps( compactable_views, this.grid_col_count );

            compactable_views.forEach( view => {
                const filled_rect = filled_rects[view.id];
                if ( filled_rect ) {
                    TTWindowLayoutPolicy.applyRect( view, filled_rect );
                }
            } );

            this.clearMismatchedCustomTrackSizes();
        },
        clearMismatchedCustomTrackSizes() {
            if ( Array.isArray( this.tt_view_stack.layout_grid?.col_sizes ) && this.tt_view_stack.layout_grid.col_sizes.length !== this.grid_col_count ) {
                this.tt_view_stack.setColSizes( null );
            }

            if ( Array.isArray( this.tt_view_stack.layout_grid?.row_sizes ) && this.tt_view_stack.layout_grid.row_sizes.length !== this.grid_row_count ) {
                this.tt_view_stack.setRowSizes( null );
            }
        },
        swapWindowPlacement( source_view, target_view ) {
            const source_rect = this.getRect( source_view );
            const target_rect = this.getRect( target_view );

            TTWindowLayoutPolicy.applyRect( source_view, target_rect );
            TTWindowLayoutPolicy.applyRect( target_view, source_rect );
            this.applyAutoFillIfEnabled();
        },
        insertGridTrackSize( axis, insert_index ) {
            if ( axis === 'column' ) {
                const next_sizes = TTWindowLayoutPolicy.insertTrackSize( this.tt_view_stack.layout_grid?.col_sizes, this.grid_col_count, insert_index );

                if ( next_sizes || Array.isArray( this.tt_view_stack.layout_grid?.col_sizes ) ) {
                    this.tt_view_stack.setColSizes( next_sizes );
                }
                return;
            }

            const next_sizes = TTWindowLayoutPolicy.insertTrackSize( this.tt_view_stack.layout_grid?.row_sizes, this.grid_row_count, insert_index );

            if ( next_sizes || Array.isArray( this.tt_view_stack.layout_grid?.row_sizes ) ) {
                this.tt_view_stack.setRowSizes( next_sizes );
            }
        },
        moveBy( id, dx, dy ) {
            const view = this.tt_view_stack.getViewById( id );
            if ( !view || !view.visible ) return;

            const r = this.getRect( view );
            this.applyPlacement( id, { x: r.x + dx, y: r.y + dy, w: r.w, h: r.h } );
        },
        resizeBy( id, dw, dh ) {
            const view = this.tt_view_stack.getViewById( id );
            if ( !view || !view.visible ) return;

            const r = this.getRect( view );
            this.applyPlacement( id, { x: r.x, y: r.y, w: r.w + dw, h: r.h + dh } );
        },
        onWindowDragStart( event, id ) {
            const view = this.tt_view_stack.getViewById( id );
            if ( !view ) return;

            this.closeMenus();
            this.resetDragState();
            this.drag.dragging_id = id;

            if ( event?.dataTransfer ) {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData( 'text/plain', id );
            }
        },

        onWindowDragEnd() {
            this.resetDragState();
        },

        resetDragState() {
            this.drag.dragging_id = null;
            this.drag.over_cell = null;
            this.drag.over_window_id = null;
            this.drag.over_intent = null;
        },
        getDragEventTargetWindowElement( event ) {
            const selector = '.layout-window[data-tt-view-id], .layout-window-controls[data-tt-view-id]';
            const event_target = event?.target?.closest?.( selector );
            if ( event_target ) {
                return event_target;
            }

            const point_target = document.elementFromPoint( event?.clientX ?? 0, event?.clientY ?? 0 );
            return point_target?.closest?.( selector ) ?? null;
        },
        getGridCellFromDragEvent( event ) {
            const grid_element = event?.currentTarget;
            if ( !grid_element ) {
                return null;
            }

            return TTWindowLayoutPolicy.getGridCellFromPointer(
                event.clientX,
                event.clientY,
                grid_element.getBoundingClientRect(),
                this.tt_view_stack.layout_grid?.col_sizes,
                this.tt_view_stack.layout_grid?.row_sizes,
                this.grid_col_count,
                this.grid_row_count
            );
        },
        isPointInsideElement( event, element ) {
            if ( !event || !element ) {
                return false;
            }

            const rect = element.getBoundingClientRect();
            return event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        },

        onGridDragOver( event ) {
            if ( !this.drag.dragging_id || !event?.currentTarget ) {
                return;
            }

            event.preventDefault();
            if ( event?.dataTransfer ) {
                event.dataTransfer.dropEffect = 'move';
            }

            const target_window_element = this.getDragEventTargetWindowElement( event );
            const target_window_id = target_window_element?.dataset?.ttViewId ?? null;
            if ( target_window_id ) {
                this.drag.over_cell = null;

                if ( target_window_id === this.drag.dragging_id ) {
                    this.drag.over_window_id = null;
                    this.drag.over_intent = null;
                    return;
                }

                this.drag.over_window_id = target_window_id;
                this.drag.over_intent = this.getWindowEdgeIntent( event, target_window_element );
                return;
            }

            this.drag.over_window_id = null;
            this.drag.over_intent = null;
            this.drag.over_cell = this.getGridCellFromDragEvent( event );
        },
        onGridDragLeave( event ) {
            if ( this.isPointInsideElement( event, event?.currentTarget ) ) {
                return;
            }

            this.drag.over_cell = null;
        },
        onGridDrop( event ) {
            const id = this.drag.dragging_id;
            if ( !id ) {
                return;
            }

            event.preventDefault();
            const target_window_element = this.getDragEventTargetWindowElement( event );
            const target_window_id = target_window_element?.dataset?.ttViewId ?? null;
            const target_intent = target_window_id ? ( this.drag.over_intent || this.getWindowEdgeIntent( event, target_window_element ) ) : null;
            const cell = this.drag.over_cell || this.getGridCellFromDragEvent( event );

            this.resetDragState();

            if ( target_window_id ) {
                if ( target_window_id !== id ) {
                    this.dropWindowOnTarget( id, target_window_id, target_intent );
                }
                return;
            }

            if ( !cell ) return;

            const view = this.tt_view_stack.getViewById( id );
            if ( !view ) {
                return;
            }

            const r = this.getRect( view );
            this.applyPlacement( id, { x: cell.x, y: cell.y, w: r.w, h: r.h } );
        },
        windowStyleVars( window_data, index ) {
            const x = Number.isInteger( window_data.x ) ? window_data.x : 0;
            const y = Number.isInteger( window_data.y ) ? window_data.y : 0;
            const w = Number.isInteger( window_data.w ) && window_data.w > 0 ? window_data.w : 1;
            const h = Number.isInteger( window_data.h ) && window_data.h > 0 ? window_data.h : 1;

            const style_vars = {
                '--grid-col-start': x + 1,
                '--grid-col-span': w,
                '--grid-row-start': y + 1,
                '--grid-row-span': h,
                '--window-z': index + 1,
            };
            const source_pane = this.getWindowSourcePane( window_data.window );
            if ( source_pane ) {
                const source_rect = TTWindowLayoutPolicy.getViewRect( source_pane );
                // CSS grid remains the geometry owner, including custom split sizes.
                // Unconfined windows retain workspace width and lean toward their
                // source side; confinement swaps only their grid placement.
                style_vars['--source-col-start'] = source_rect.x + 1;
                style_vars['--source-col-span'] = source_rect.w;
                style_vars['--source-row-start'] = source_rect.y + 1;
                style_vars['--source-row-span'] = source_rect.h;
                style_vars['--source-margin-left'] = source_rect.x + source_rect.w / 2 > this.grid_col_count / 2 ? 'auto' : '0';
                style_vars['--source-margin-right'] = source_rect.x + source_rect.w / 2 > this.grid_col_count / 2 ? '0' : 'auto';
            }
            const edit_shell_height = this.getWindowEditShellHeight( window_data.window.id );
            const edit_shell_heights_by_mode = this.getWindowEditShellHeightsByMode( window_data.window.id );
            const edit_shell_width = this.getWindowEditShellWidth( window_data.window.id );
            const wizard_shell_height = this.getWindowWizardShellHeight( window_data.window.id );
            const wizard_shell_width = this.getWindowWizardShellWidth( window_data.window.id );

            if ( edit_shell_height ) {
                style_vars['--modal-shell-locked-height'] = edit_shell_height;
            }

            if ( edit_shell_heights_by_mode ) {
                Object.entries( edit_shell_heights_by_mode ).forEach( ( [mode, height] ) => {
                    style_vars['--modal-shell-height-' + mode.replace( /_/g, '-' )] = height;
                } );
            }

            if ( edit_shell_width || wizard_shell_width ) {
                // Keep configured edit widths separate from the rendered width token; lane classes define --modal-shell-width on .window-shell, so an inherited value with the same name would lose in the cascade.
                style_vars['--modal-shell-configured-width'] = edit_shell_width || wizard_shell_width;
            }

            if ( wizard_shell_height ) {
                style_vars['--modal-shell-wizard-height'] = wizard_shell_height;
            }

            const shell_layout = this.shell_layouts[window_data.window.id];
            // Schema dimensions use the existing modal tokens, preserving the one
            // TTWindowManager card instead of introducing owner-local sizing shells.
            if ( shell_layout?.width ) {
                style_vars['--modal-shell-configured-width'] = shell_layout.width;
            }
            if ( shell_layout?.height ) {
                style_vars['--modal-shell-locked-height'] = shell_layout.height;
            }

            return style_vars;
        },
        getWindowAction( id ) {
            const window_view = this.tt_view_stack.getViewById( id );
            // Issue #3864: This is a local layout/focus policy classification, not
            // a route mutation. An explicit route-less task gets the established
            // Shell sizing, first-paint and guarded-close behavior used by managed
            // shells. Its existing route, if any, always wins (e.g. Payroll remains
            // a wizard with workflow-owned cancellation, not a generic shell).
            // Do not create TTRouteData just to obtain controls: that would give a
            // live unsaved task an invented URL/schema/persistence identity. Actual
            // module/action reporting still reads route metadata through its normal
            // accessors, and TTViewStack skips URL synchronization for these hosts.
            return window_view?.tt_route_data?.getAction?.() ?? ( window_view?.window_controls === true ? 'shell' : null );
        },
        isModalWindow( id ) {
            const window_view = this.tt_view_stack.getViewById( id );
            return this.tt_view_stack.isModalView( window_view ) === true;
        },
        isWizardWindow( id ) {
            return this.getWindowAction( id ) === 'wizard';
        },
        isTTSShellWindow( id ) {
            return this.getWindowAction( id ) === 'shell';
        },
        isManagedFocusWindow( id ) {
            // Converted modal content remains routed as `wizard`; both it and
            // TTSShell share this one window-level containment owner.
            return this.isWizardWindow( id ) || this.isTTSShellWindow( id );
        },
        // Edit/new/view modals are sized from route metadata before reveal, then locked so async tabs/fields do not resize the visible shell.
        requiresEditLaneSizing( id ) {
            return ['edit', 'new', 'view'].includes( this.getWindowAction( id ) );
        },
        isEditLaneSizingPending( id ) {
            if ( !this.isModalWindow( id ) || !this.requiresEditLaneSizing( id ) ) {
                return false;
            }

            const edit_lane_count = this.getWindowEditLaneCount( id );
            if ( !edit_lane_count ) {
                return true;
            }

            return edit_lane_count !== 3 && !this.getWindowEditShellWidth( id );
        },
        getWindowEditLaneCount( id ) {
            if ( this.locked_edit_lane_counts[id] ) {
                return this.locked_edit_lane_counts[id];
            }

            const lane_counts = Object.values( this.edit_lane_counts[id] || {} );

            if ( lane_counts.length === 0 ) {
                return null;
            }

            return Math.max( ...lane_counts );
        },
        getWindowEditShellHeight( id ) {
            if ( this.locked_edit_shell_heights[id] ) {
                return this.locked_edit_shell_heights[id];
            }

            const shell_heights = Object.values( this.edit_shell_heights[id] || {} );

            if ( shell_heights.length === 0 ) {
                return null;
            }

            return shell_heights[0];
        },
        getWindowEditShellHeightsByMode( id ) {
            if ( this.locked_edit_shell_heights_by_mode[id] ) {
                return this.locked_edit_shell_heights_by_mode[id];
            }

            const mode_heights = Object.values( this.edit_shell_heights_by_mode[id] || {} );

            if ( mode_heights.length === 0 ) {
                return null;
            }

            return mode_heights[0];
        },
        getWindowEditShellWidth( id ) {
            if ( this.locked_edit_shell_widths[id] ) {
                return this.locked_edit_shell_widths[id];
            }

            const shell_widths = Object.values( this.edit_shell_widths[id] || {} );

            if ( shell_widths.length === 0 ) {
                return null;
            }

            return shell_widths[0];
        },
        getWindowWizardShellHeight( id ) {
            return this.wizard_shell_heights[id] || null;
        },
        getWindowWizardShellWidth( id ) {
            return this.wizard_shell_widths[id] || null;
        },
        windowClasses( id ) {
            const edit_lane_count = this.getWindowEditLaneCount( id );
            const edit_shell_height = this.getWindowEditShellHeight( id );
            const edit_shell_heights_by_mode = this.getWindowEditShellHeightsByMode( id );
            const wizard_shell_height = this.getWindowWizardShellHeight( id );

            return {
                active: id === this.active_view_id,
                'is-dragging': id === this.drag.dragging_id,
                'is-menu-open': id === this.menu.active_id,
                'is-modal-window': this.isModalWindow( id ),
                // These classes describe geometry/depth only; coverage and ARIA
                // continue to use the independent active-modal companion predicate.
                'is-record-modal': this.isModalWindow( id ) && this.requiresEditLaneSizing( id ),
                'is-pane-confined': this.float_within_pane[id] !== false && !!this.getWindowSourcePane( this.tt_view_stack.getViewById( id ) ),
                'is-covered-by-modal': this.isWindowCoveredByActiveModal( this.tt_view_stack.getViewById( id ) ),
                'is-wizard-window': this.isWizardWindow( id ),
                'is-wizard-shell-height-configured': !!wizard_shell_height,
                'is-wizard-shell-content-sized': this.content_sized_wizard_shells[id] === true,
                'is-tts-shell-window': this.isTTSShellWindow( id ),
                'is-side-panel-window': this.isSidePanelWindow( this.tt_view_stack.getViewById( id ) ),
                'is-edit-lanes-1': edit_lane_count === 1,
                'is-edit-lanes-2': edit_lane_count === 2,
                'is-edit-lanes-3': edit_lane_count === 3,
                'is-edit-shell-height-locked': !!edit_shell_height || !!edit_shell_heights_by_mode,
                'is-tts-shell-height-locked': !!this.shell_layouts[id]?.height,
                // CSS owns the actual intrinsic/flex sizing. This class only carries
                // TTViewLoader's schema-level eligibility decision across the
                // component boundary; it is deliberately independent of rendered
                // scroll geometry and remains stable for the window lifetime.
                'is-edit-shell-content-sized': this.content_sized_edit_shells[id] === true,
                'is-edit-lane-size-pending': this.isEditLaneSizingPending( id ),
            };
        },
        windowControlClasses( id ) {
            return {
                active: id === this.active_view_id,
                'is-dragging': id === this.drag.dragging_id,
                'is-menu-open': id === this.menu.active_id,
                'is-covered-by-modal': this.isWindowCoveredByActiveModal( this.tt_view_stack.getViewById( id ) ),
                'is-wizard-window': this.isWizardWindow( id ),
                // Issue #3752: Docking moves routed-shell caption controls outside .layout-window; keep their title-inset styling on this separate grid layer.
                'is-tts-shell-window': this.isTTSShellWindow( id ),
            };
        },
        gridIndicatorVars() {
            if ( this.drag.over_cell ) {
                return {
                    '--indicator-col': this.drag.over_cell.x + 1,
                    '--indicator-row': this.drag.over_cell.y + 1,
                    '--indicator-col-span': 1,
                    '--indicator-row-span': 1,
                };
            }

            if ( this.drag.over_window_id && this.drag.over_intent ) {
                const target = this.tt_view_stack.getViewById( this.drag.over_window_id );
                if ( !target ) {
                    return null;
                }

                const t = this.getRect( target );
                const source = this.drag.dragging_id ? this.tt_view_stack.getViewById( this.drag.dragging_id ) : null;
                const source_rect = source ? this.getRect( source ) : { w: 1 };
                const column_drop_span = this.getColumnDropSpan( source_rect );
                const column_drop_row_span = TTWindowLayoutPolicy.normalizePositiveInteger( source_rect.h, t.h );

                if ( this.drag.over_intent === 'col-left' ) {
                    return {
                        '--indicator-col': t.x + 1,
                        '--indicator-row': t.y + 1,
                        '--indicator-col-span': column_drop_span,
                        '--indicator-row-span': column_drop_row_span,
                    };
                }

                if ( this.drag.over_intent === 'col-right' ) {
                    return {
                        '--indicator-col': ( t.x + t.w ) + 1,
                        '--indicator-row': t.y + 1,
                        '--indicator-col-span': column_drop_span,
                        '--indicator-row-span': column_drop_row_span,
                    };
                }

                if ( this.drag.over_intent === 'row-above' ) {
                    return {
                        '--indicator-col': 1,
                        '--indicator-row': t.y + 1,
                        '--indicator-col-span': this.grid_col_count,
                        '--indicator-row-span': 1,
                    };
                }

                if ( this.drag.over_intent === 'row-below' ) {
                    return {
                        '--indicator-col': 1,
                        '--indicator-row': ( t.y + t.h ) + 1,
                        '--indicator-col-span': this.grid_col_count,
                        '--indicator-row-span': 1,
                    };
                }
            }

            return null;
        },
        getWindowEdgeIntent( event, target_element = null ) {
            const target = target_element || event?.currentTarget;
            if ( !target ) {
                return 'swap';
            }

            const rect = target.getBoundingClientRect();
            const w = rect.width || 1;
            const h = rect.height || 1;

            const x_ratio = ( event.clientX - rect.left ) / w;
            const y_ratio = ( event.clientY - rect.top ) / h;

            // Edge zones need to be large enough for drag/drop, while preserving a
            // clear center area where dropping means "swap these two windows".
            const edge_x = 0.18;
            const edge_y = 0.22;

            if ( x_ratio <= edge_x ) {
                return 'col-left';
            }
            if ( x_ratio >= 1 - edge_x ) {
                return 'col-right';
            }
            if ( y_ratio <= edge_y ) {
                return 'row-above';
            }
            if ( y_ratio >= 1 - edge_y ) {
                return 'row-below';
            }

            return 'swap';
        },
        rectsShareRowBand( a, b ) {
            return a.y < ( b.y + b.h ) && ( a.y + a.h ) > b.y;
        },
        getColumnDropSpan( source_rect ) {
            return this.auto_fill_enabled === true ? 1 : TTWindowLayoutPolicy.normalizePositiveInteger( source_rect?.w, 1 );
        },
        insertColumnInRow( target_rect, insert_x, insert_span = 1, exclude_id = null ) {
            const row_rect = TTWindowLayoutPolicy.normalizeRect( target_rect );
            const ix = Number.isInteger( insert_x ) ? Math.max( 0, insert_x ) : 0;
            const shift = TTWindowLayoutPolicy.normalizePositiveInteger( insert_span, 1 );

            this.getManagedLayoutViews().forEach( v => {
                if ( !v || v.id === exclude_id ) {
                    return;
                }

                const rect = this.getRect( v );
                if ( this.rectsShareRowBand( rect, row_rect ) && rect.x >= ix ) {
                    v.window_column = rect.x + shift;
                }
            } );

            this.clearMismatchedCustomTrackSizes();
        },

        insertColumnAt( insert_x, exclude_id = null ) {
            const ix = Number.isInteger( insert_x ) ? insert_x : 0;

            this.insertGridTrackSize( 'column', ix );
            this.getManagedLayoutViews().forEach( v => {
                if ( !v || v.id === exclude_id ) {
                    return;
                }

                const rect = this.getRect( v );
                if ( rect.x >= ix ) {
                    v.window_column = rect.x + 1;
                }
            } );
        },

        insertRowAt( insert_y, exclude_id = null ) {
            const iy = Number.isInteger( insert_y ) ? insert_y : 0;

            this.insertGridTrackSize( 'row', iy );
            this.getManagedLayoutViews().forEach( v => {
                if ( !v || v.id === exclude_id ) {
                    return;
                }

                const rect = this.getRect( v );
                if ( rect.y >= iy ) {
                    v.window_row = rect.y + 1;
                }
            } );
        },
        onWindowDragOver( event, id ) {
            if ( !this.drag.dragging_id || this.drag.dragging_id === id ) {
                this.drag.over_cell = null;
                this.drag.over_window_id = null;
                this.drag.over_intent = null;
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            this.drag.over_cell = null;
            this.drag.over_window_id = id;
            this.drag.over_intent = this.getWindowEdgeIntent( event );
            if ( event?.dataTransfer ) {
                event.dataTransfer.dropEffect = 'move';
            }
        },
        onWindowDragLeave( event, id ) {
            if ( this.isPointInsideElement( event, event?.currentTarget ) ) {
                return;
            }

            if ( this.drag.over_window_id === id ) {
                this.drag.over_window_id = null;
                this.drag.over_intent = null;
            }
        },
        dropWindowOnTarget( source_id, target_id, intent ) {
            if ( !source_id || source_id === target_id ) {
                return;
            }

            const source_view = this.tt_view_stack.getViewById( source_id );
            const target_view = this.tt_view_stack.getViewById( target_id );

            if ( !source_view || !target_view ) {
                return;
            }

            const s = this.getRect( source_view );
            const t = this.getRect( target_view );

            if ( intent === 'col-left' || intent === 'col-right' ) {
                const insert_x = t.x + ( intent === 'col-right' ? t.w : 0 );
                const column_drop_span = this.getColumnDropSpan( s );
                const proposed_rect = {
                    x: insert_x,
                    y: t.y,
                    w: column_drop_span,
                    h: s.h,
                };

                this.insertColumnInRow( proposed_rect, insert_x, column_drop_span, source_id );
                this.applyPlacement( source_id, proposed_rect );

                return;
            }

            if ( intent === 'row-above' || intent === 'row-below' ) {
                const insert_y = t.y + ( intent === 'row-below' ? t.h : 0 );

                this.insertRowAt( insert_y, source_id );

                this.applyPlacement( source_id, {
                    x: t.x,
                    y: insert_y,
                    w: s.w,
                    h: s.h,
                } );

                return;
            }
            this.swapWindowPlacement( source_view, target_view );
        },
        onWindowDrop( event, id ) {
            const source_id = this.drag.dragging_id;
            if ( !source_id ) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            const target_id = id;
            const intent = this.drag.over_intent;

            this.resetDragState();

            this.dropWindowOnTarget( source_id, target_id, intent );
        },
    },
};
</script>
<style scoped>
.tt-window-manager {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    /* Window z-index tiers are only for windows inside the content workspace.
       Keep them from competing with the topbar/sidebar shell layers. */
    isolation: isolate;
    z-index: 0;
}

.layout-window-grid {
    display: grid;
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;

    grid-template-columns: var(--col-template, repeat(var(--col-count, 1), minmax(0, 1fr)));
    grid-template-rows: var(--row-template, repeat(var(--row-count, 1), minmax(0, 1fr)));

    gap: 1px;
    background: var(--tt-color-surface-muted);

    position: relative;
}

.layout-window {
    /* Issue #3894: Theme the neutral DOM surface; inverse foregrounds and state fills keep their own roles. */
    display: flex;
    flex-direction: column;
    background: var(--tt-color-surface);
    position: relative;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    z-index: var(--window-z, 1);

    grid-column: var(--grid-col-start, 1) / span var(--grid-col-span, 1);
    grid-row: var(--grid-row-start, 1) / span var(--grid-row-span, 1);
}

/* The visible gutter stays restrained while the transparent 11px button gives
   pointer, touch, and keyboard users a practical target on either side. */
.layout-grid-splitter {
    position: absolute;
    z-index: 80;
    display: block;
    margin: 0;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    touch-action: none;
    user-select: none;
}

.layout-grid-splitter::before {
    content: '';
    position: absolute;
    background: var(--tt-color-border-strong);
    opacity: 0;
    transition: opacity 0.12s ease, background-color 0.12s ease;
}

.layout-grid-splitter:hover::before,
.layout-grid-splitter:focus-visible::before,
.layout-grid-splitter.is-resizing::before {
    opacity: 1;
    background: var(--tt-color-accent);
}

.layout-grid-splitter:focus-visible {
    outline: 2px solid rgb(from var(--tt-color-accent-text) r g b / 0.62);
    outline-offset: -2px;
}

.layout-grid-splitter-column {
    top: var(--splitter-cross-start);
    left: var(--splitter-x);
    width: 11px;
    height: var(--splitter-cross-size);
    cursor: col-resize;
    transform: translateX( -50% );
}

.layout-grid-splitter-column::before {
    top: 0;
    bottom: 0;
    left: 5px;
    width: 1px;
}

.layout-grid-splitter-row {
    left: var(--splitter-cross-start);
    top: var(--splitter-y);
    width: var(--splitter-cross-size);
    height: 11px;
    cursor: row-resize;
    transform: translateY( -50% );
}

.layout-grid-splitter-row::before {
    right: 0;
    bottom: 5px;
    left: 0;
    height: 1px;
}

.layout-window.is-modal-window {
    --modal-shell-gutter: clamp( 3px, 0.4vw, 5px );
    --modal-shell-block-padding: clamp( 12px, 2vw, 24px );
    --modal-shell-topbar-height: 50px;
    --modal-shell-visible-height: calc( 100vh - var(--modal-shell-topbar-height) );
    --modal-shell-visible-height: calc( 100dvh - var(--modal-shell-topbar-height) );
    --modal-shell-content-visible-height: max(
        0px,
        calc( var(--modal-shell-visible-height) - ( var(--modal-shell-block-padding) * 2 ) )
    );

    background: transparent;
    /* Use modal workspace width instead of viewport width; the left menu can
       make edit fields hit stacked-label breakpoints before the browser does. */
    container-name: modalwindow;
    container-type: inline-size;
    box-sizing: border-box;
    /* Dashboard-launched modals can inherit the app's desktop min-height. Cap
       the shell to the visible workspace below the fixed topbar. */
    max-height: max( 0px, var(--modal-shell-visible-height) );
    overflow: visible;
    align-items: center;
    justify-content: center;
    /* Keep vertical breathing room, but minimize horizontal backdrop gutters so
       two-lane edit forms do not collapse just before the 62rem content breakpoint. */
    padding: var(--modal-shell-block-padding) var(--modal-shell-gutter);
    z-index: calc( 30 + var(--window-z, 1) );
    grid-column: 1 / -1 !important;
    grid-row: 1 / -1 !important;
}

.layout-window-grid.has-active-modal-side-panel > .layout-window.is-modal-window {
    /* A modal-launched companion keeps the overlay in its primary grid cell so
       the ordinary right pane remains visible and interactive beside it. */
    grid-column: var(--grid-col-start) / span var(--grid-col-span) !important;
    grid-row: var(--grid-row-start) / span var(--grid-row-span) !important;
}

.layout-window-grid > .layout-window.is-modal-window.is-record-modal {
    /* The backdrop cannot intercept a still-interactive companion.
       Existing covered/inert rules continue blocking unrelated background panes. */
    pointer-events: none;
    /* Visual anchoring never changes companion interactivity. An unchecked
       editor may span even a companion layout; its shell leans toward its source. */
    grid-column: 1 / -1 !important;
    grid-row: 1 / -1 !important;
}

.is-record-modal:not(.is-covered-by-modal) > .window-shell {
    /* Restore hit testing only to usable record shells, never covered editors
       underneath a nested modal. The surrounding backdrop remains pointer-transparent.
       Keep specificity below the sizing/paint gates so invisible shells cannot
       receive input before their existing readiness contract allows it. */
    pointer-events: auto;
}

.layout-window-grid > .layout-window.is-modal-window.is-record-modal.is-pane-confined {
    /* Hard bounds follow the source grid cell, including right and row panes.
       The existing inset backdrop follows these same bounds: confined editors
       blur only their source pane, while unconfined editors blur the whole grid.
       Container queries now see the pane width and adapt the existing edit form. */
    grid-column: var(--source-col-start) / span var(--source-col-span) !important;
    grid-row: var(--source-row-start) / span var(--source-row-span) !important;
}

.layout-window.is-modal-window.is-record-modal:not(.is-pane-confined) > .window-shell {
    margin-left: var(--source-margin-left, auto);
    margin-right: var(--source-margin-right, auto);
}

.layout-window.is-modal-window.active {
    z-index: calc( 60 + var(--window-z, 1) );
}

.layout-window.active:not(.is-modal-window):not(.is-covered-by-modal),
.layout-window.is-dragging:not(.is-modal-window):not(.is-covered-by-modal) {
    /* Drag/drop can create temporary visual overlap between list windows; keep
       the active/dragged window chrome above neighboring grid items. */
    z-index: calc( 40 + var(--window-z, 1) );
}

.layout-window.is-covered-by-modal {
    pointer-events: none;
}

.layout-window-controls {
    position: relative;
    min-width: 0;
    min-height: 0;
    pointer-events: none;
    z-index: calc( 90 + var(--window-z, 1) );

    grid-column: var(--grid-col-start, 1) / span var(--grid-col-span, 1);
    grid-row: var(--grid-row-start, 1) / span var(--grid-row-span, 1);
}

.layout-window-controls.is-menu-open {
    /* Keep menu chrome out of list/context-border clipping while preserving the
       window grid as the only place these controls can layer above content. */
    z-index: calc( 120 + var(--window-z, 1) );
}

.layout-window-controls.is-covered-by-modal {
    pointer-events: none;
}

.layout-window-controls.is-covered-by-modal .window-menu,
.layout-window-controls.is-covered-by-modal .window-menu-dropdown {
    pointer-events: none;
}

.layout-window.is-modal-window::before {
    content: '';
    position: absolute;
    inset: 0;
    /* Issue #3894: Keep occlusion dark while reading text changes; retain the existing alpha. */ background: rgb(from var(--tt-color-shadow) r g b / 0.34);
    opacity: 0;
    transition: opacity 0.18s ease;
    backdrop-filter: blur( 4px );
    -webkit-backdrop-filter: blur( 4px );
}

.layout-window.is-modal-window.active::before {
    opacity: 1;
}

.layout-window.is-wizard-window {
    background: transparent;
}

.window-shell {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
}

.layout-window.is-modal-window > .window-shell {
    --modal-shell-width: var(--modal-shell-configured-width, 80%);

    flex: 0 1 auto;
    width: min( var(--modal-shell-width), calc( 100% - ( var(--modal-shell-gutter) * 2 ) ) );
    max-width: 100%;
    height: min( 100%, var(--modal-shell-content-visible-height) );
    max-height: min( 100%, var(--modal-shell-content-visible-height) );
    margin: 0 auto auto;
}

.layout-window.is-modal-window.is-wizard-window > .window-shell {
    /* Wizards use the available workspace as a task surface rather than an edit-form modal.
       The minimized-view tray is fixed 4px above the viewport and sits outside this
       modal layout. Reserving 20px from both the actual containing workspace and the
       viewport cap moves the Wizard's bottom edge above the tray in static, slim, and
       horizontal-menu layouts, leaving a small visible gap without shortening edit,
       list, or report shells. Keep this reserve at the shared Wizard owner so every
       default-height Wizard follows the same bottom-left collision contract. */
    --modal-shell-width: var(--modal-shell-configured-width, 90%);
    --modal-shell-wizard-bottom-clearance: 20px;
    --modal-shell-wizard-container-visible-height: max(
        0px,
        calc( 100% - var(--modal-shell-wizard-bottom-clearance) )
    );
    --modal-shell-wizard-viewport-visible-height: max(
        0px,
        calc( var(--modal-shell-content-visible-height) - var(--modal-shell-wizard-bottom-clearance) )
    );

    height: min( var(--modal-shell-wizard-container-visible-height), var(--modal-shell-wizard-viewport-visible-height) );
    max-height: min( var(--modal-shell-wizard-container-visible-height), var(--modal-shell-wizard-viewport-visible-height) );
}

.layout-window.is-modal-window.is-wizard-window.is-wizard-shell-height-configured > .window-shell {
    /* Explicit schema heights still respect the same tray-safe viewport cap. */
    height: min( var(--modal-shell-wizard-height), var(--modal-shell-wizard-container-visible-height), var(--modal-shell-wizard-viewport-visible-height) );
    max-height: min( var(--modal-shell-wizard-height), var(--modal-shell-wizard-container-visible-height), var(--modal-shell-wizard-viewport-visible-height) );
}

.layout-window.is-modal-window.is-wizard-window.is-wizard-shell-content-sized > .window-shell {
    /* Compact TTS Wizards grow with their authored body until the shared tray-safe cap; this keeps the outer white modal card aligned with the actual workflow instead of leaving a full-workspace canvas around it. */
    height: auto;
    max-height: min( var(--modal-shell-wizard-container-visible-height), var(--modal-shell-wizard-viewport-visible-height) );
}

.layout-window.is-modal-window.is-wizard-window.is-wizard-shell-content-sized > .window-shell > .stacked-view {
    /* The row-flex parent uses this flex value only for width; intrinsic block height still comes from the active Wizard while the cap preserves internal scrolling for genuinely tall content. */
    flex: 1 1 auto;
    width: 100%;
    max-height: 100%;
}

.layout-window.is-modal-window.is-wizard-window > .window-shell > .stacked-view {
    border-radius: 8px;
    box-shadow: 0 18px 46px rgb(from var(--tt-color-heading) r g b / 0.24), 0 2px 8px rgb(from var(--tt-color-heading) r g b / 0.12);
}

/* Lane-specific modal widths are applied only after TTViewLoader/EditRecords
   report stable metadata. Three-lane and unknown edit layouts keep the broader
   80% fallback to avoid under-sizing dense or not-yet-classified forms. */
.layout-window.is-modal-window.is-edit-lanes-1 > .window-shell {
    --modal-shell-width: var(--modal-shell-configured-width, 64rem);
    --modal-shell-responsive-height: var(--modal-shell-height-lanes-1, var(--modal-shell-locked-height, 100%));
}

.layout-window.is-modal-window.is-edit-lanes-2 > .window-shell {
    --modal-shell-width: var(--modal-shell-configured-width, 96rem);
    --modal-shell-responsive-height: var(--modal-shell-height-lanes-2, var(--modal-shell-height-lanes-1, var(--modal-shell-locked-height, 100%)));
}

.layout-window.is-modal-window.is-edit-lanes-3 > .window-shell {
    --modal-shell-responsive-height: var(--modal-shell-height-lanes-3, var(--modal-shell-height-lanes-2, var(--modal-shell-locked-height, 100%)));
}

:global(.layout-window.is-modal-window.is-edit-shell-content-sized.is-edit-lanes-1 > .window-shell:has(.tab-footer-slot .multi-row-editor)),
:global(.layout-window.is-modal-window.is-edit-shell-content-sized.is-edit-lanes-2 > .window-shell:has(.tab-footer-slot .multi-row-editor)),
:global(.layout-window.is-modal-window.is-edit-shell-content-sized.is-edit-lanes-3 > .window-shell:has(.tab-footer-slot .multi-row-editor)) {
    /* Footer row editors are outside TTViewLoader's parent-field estimate, so
       widen unconfigured modals from rendered frontend content while still honoring view-specific shell widths. */
    --modal-shell-width: var(--modal-shell-configured-width, 96rem);
    --modal-shell-responsive-height: 100%;
}

.layout-window.is-modal-window.is-edit-shell-height-locked > .window-shell {
    height: min( var(--modal-shell-responsive-height, var(--modal-shell-locked-height, 100%)), 100% );
}

.layout-window.is-modal-window.is-tts-shell-height-locked > .window-shell {
    /* Authored shell height is a presentation contract, not a lane estimate; keep
       it stable while owner content and datasource options load asynchronously. */
    height: min( var(--modal-shell-locked-height, 100%), 100% );
}

.layout-window.is-modal-window.is-edit-shell-height-locked.is-edit-shell-content-sized > .window-shell {
    /*
     * Automatic ordinary edit shells use the metadata estimate as their first-paint
     * floor, not as an exact clipping boundary. Intrinsic content may take available
     * space until the same viewport cap as every modal is reached; after that point
     * the descendant `.edit-view-content` remains the single vertical scroll owner.
     *
     * Do not broaden this selector to explicitly sized/full-row workspaces. Their
     * fixed canvas is a product contract for maps, rich-text editors, tables, and
     * other embedded surfaces, whereas this mode is a safety valve for normal TTS
     * field rows and small future box-model drift. The containing-height cap also
     * keeps a confined editor inside a short row pane rather than the full viewport.
     */
    height: auto;
    min-height: min(
        var(--modal-shell-responsive-height, var(--modal-shell-locked-height, 100%)),
        var(--modal-shell-content-visible-height),
        100%
    );
    max-height: min( 100%, var(--modal-shell-content-visible-height) );
}

.layout-window.is-modal-window.is-edit-shell-content-sized > .window-shell > .stacked-view {
    /*
     * `.window-shell` is a row flex container, so its child's flex value controls
     * horizontal width, not the intrinsic-height policy. Keep the stacked view
     * filling that row; changing it to a content-width flex basis narrows a 96rem
     * two-lane shell to one lane and recreates the very overflow this mode prevents.
     * Height still comes from the active in-flow tab and remains capped here.
     */
    flex: 1 1 auto;
    max-height: 100%;
    width: 100%;
}

.layout-window.is-modal-window.is-edit-shell-content-sized > .window-shell > .stacked-view > .context-border {
    /*
     * ContextMenuWithBorder's legacy baseline carries `height: calc(100% - 35px)` for static
     * list/report shells. That percentage severs intrinsic height propagation here:
     * it resolves the border from its parent instead of its active edit content.
     * Override only the direct edit-window border; nested subview borders keep their
     * existing table/list contracts.
     */
    height: auto;
    min-height: 0;
}

@container modalwindow (max-width: 120rem) {
    .layout-window.is-modal-window.is-edit-lanes-3.is-edit-shell-height-locked > .window-shell {
        /* Three-lane modal shells use the broad 80% fallback width; below this
           workspace size shell/tab gutters put EditRecords at its 93rem two-lane breakpoint. */
        --modal-shell-responsive-height: var(--modal-shell-height-lanes-2, var(--modal-shell-height-lanes-1, var(--modal-shell-locked-height, 100%)));
    }
}

@container modalwindow (max-width: 68.5rem) {
    .layout-window.is-modal-window.is-edit-shell-height-locked > .window-shell {
        /* Calibrated on TimeSheet Edit Punch with the left menu visible; below
           this modal width, shell chrome/root sizing put EditRecords under its
           effective 62rem two-lane breakpoint. */
        --modal-shell-responsive-height: var(--modal-shell-height-lanes-1, var(--modal-shell-locked-height, 100%));
    }
}

@media (max-width: 72rem) {
    .layout-window-grid:not(.has-active-modal-side-panel) > .layout-window.is-modal-window.is-tts-shell-window {
        /* The legacy application canvas keeps a desktop minimum width on small
           viewports and may be horizontally scrolled by board interactions.
           Anchor routed shells to the actual visual viewport instead of that
           off-screen canvas. A shell with a companion must retain its primary
           grid cell instead, so it cannot cover the still-interactive pane. */
        align-items: flex-start;
        position: fixed;
        inset: var(--modal-shell-topbar-height) auto 0 0;
        width: 100vw;
        height: var(--modal-shell-visible-height);
    }

    .layout-window-grid:not(.has-active-modal-side-panel) > .layout-window.is-modal-window.is-tts-shell-window > .window-shell {
        width: calc( 100vw - ( var(--modal-shell-gutter) * 2 ) );
        max-width: calc( 100vw - ( var(--modal-shell-gutter) * 2 ) );
        margin-right: 0;
        margin-left: 0;
    }

    .layout-window.is-modal-window.is-edit-shell-height-locked > .window-shell {
        /* Mirrors EditRecords' one-column viewport fallback for desktop-min-width
           layouts where the form container may stay above its 62rem lane query. */
        --modal-shell-responsive-height: var(--modal-shell-height-lanes-1, var(--modal-shell-locked-height, 100%));
    }
}

@container modalwindow (max-width: 35rem) {
    .layout-window.is-modal-window.is-edit-shell-height-locked > .window-shell {
        /* Regular edit fields switch labels above inputs once the edit content
           nears a 30rem lane; account for shell gutters/chrome here. */
        --modal-shell-responsive-height: var(--modal-shell-height-lanes-1-stacked, var(--modal-shell-height-lanes-1, var(--modal-shell-locked-height, 100%)));
    }
}

@media (max-width: 32rem) {
    .layout-window.is-modal-window.is-edit-shell-height-locked > .window-shell {
        /* Mirrors EditRecords' stacked-label viewport fallback for desktop-min-width
           layouts where the form container can stay above its lane-width query. */
        --modal-shell-responsive-height: var(--modal-shell-height-lanes-1-stacked, var(--modal-shell-height-lanes-1, var(--modal-shell-locked-height, 100%)));
    }
}

.layout-window.is-modal-window.is-edit-lane-size-pending > .window-shell {
    /* Hide the shell until lane width metadata exists; otherwise edit modals flash
       at the broad fallback and then shrink after TTS/layout data arrives. Keep this
       established fallback in its own rule so a browser without :has() support does
       not discard the lane-size protection along with the newer empty-content gate.
       Issue #3752: visibility:hidden also prevents focus from entering an edit
       toolbar that cannot yet be shown at its settled width. */
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
}

:global(.layout-window.is-modal-window:has(> .window-shell > .stacked-view [data-tt-window-paint-state='pending']):not(:has(> .window-shell > .stacked-view > [data-tt-window-paint-state='ready'])) > .window-shell) {
    /*
     * Issue #3752: make every routed modal wait for its owner certificate.
     * Custom edit and task-shell bodies can depend on several APIs after their route component has
     * already mounted. In that case the ordinary router-view and lane-size gates are
     * both truthfully complete, but exposing the shell would show its context menu,
     * empty fields, and empty tables as separate browser paints.
     *
     * All routed modal actions use a first-paint certificate. A ready root
     * excludes later pending markers inside a nested tab or child dialog.
     *
     * Keep the complete shell hidden, including caption and toolbar chrome, because
     * revealing either before the body recreates the exact chrome-first/body-second
     * flash this contract exists to prevent. Visibility also keeps pending
     * controls out of the focus and accessibility trees.
     */
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
}

.layout-window.is-modal-window:not(:has(> .window-shell > .stacked-view > *)) > .window-shell {
    /* Issue #3752: named route components can mount after the outer card; keep the backdrop
       but withhold blank chrome until any modal action has a real component. */
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
}

:global(.direct-shell-overlay:has(> .direct-shell-dialog > [data-tt-window-paint-state='pending']) > .direct-shell-dialog > [data-tt-window-paint-state='pending']) {
    /* Keep pending authentication controls out of Tab order before reveal. */
    visibility: hidden;
}

:global(.direct-shell-overlay:has(> .direct-shell-dialog > [data-tt-window-paint-state='pending']) > .direct-shell-dialog) {
    /* Issue #3768: retain the dimmed keyboard boundary during first paint,
       without flashing an empty white dialog after the status card is removed. */
    opacity: 0;
    pointer-events: none;
}

.stacked-view {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    z-index: var(--window-z, 1);
}

.layout-window.is-modal-window > .window-shell > .stacked-view {
    /* Issue #3894: Theme the neutral DOM surface; inverse foregrounds and state fills keep their own roles. */
    background: var(--tt-color-surface);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 28px 70px rgb(from var(--tt-color-black) r g b / 0.24);
}

.layout-drop-indicator {
    pointer-events: none;
    position: relative;
    z-index: 80;

    border: 2px dashed var(--tt-color-accent);
    background: rgb(from var(--tt-color-accent) r g b / 0.08);

    grid-column: var(--indicator-col, 1) / span var(--indicator-col-span, 1);
    grid-row: var(--indicator-row, 1) / span var(--indicator-row-span, 1);
}

.window-menu {
    position: absolute;
    display: flex;
    /*
     * The context-border rule sits 20px inside each managed shell. A 28px
     * caption button centered from top: 6px lands on that rule exactly. Inset
     * the group 15px inside the rule to mirror the edit shell label text's
     * left inset and prevent outline spill. Keep this geometry independent
     * from TTContextMenu so window chrome cannot move or compress view-owned
     * actions.
     */
    gap: 0;
    top: 6px;
    right: 35px;
    z-index: 6;
    pointer-events: auto;
}

/*
 * Keep context actions in their view-owned horizontal positions while giving
 * shell caption controls a clear second row. Nested context borders do not
 * carry caption chrome, so retain their established 10px inset.
 */
.layout-window:not(.is-wizard-window) > .window-shell > .stacked-view :deep(.context-border) {
    padding-top: 20px;
}

.layout-window:not(.is-wizard-window) > .window-shell > .stacked-view :deep(.context-border .context-border) {
    padding-top: 10px;
}

.layout-window.is-modal-window .window-menu {
    top: 6px;
    /* Issue #3752: Edit modals keep the context-border inset; routed TTS shells align to their own title below. */
    right: 35px;
    z-index: calc( 80 + var(--window-z, 1) );
}

.layout-window.is-modal-window.is-tts-shell-window > .window-shell,
.layout-window-controls.is-tts-shell-window {
    /* Issue #3752: Centered and docked captions use different parents; both must query the width of the pane containing TTSShellFrame's title. */
    container-name: tts-shell-window;
    container-type: inline-size;
}

.layout-window.is-modal-window.is-tts-shell-window .window-menu,
.layout-window-controls.is-tts-shell-window .window-menu {
    /* Issue #3752: Mirror TTSShellFrame's 22px title inset in either chrome location without shifting ordinary edit controls. */
    right: 22px;
}

@container tts-shell-window (max-width: 34rem) {
    .layout-window.is-modal-window.is-tts-shell-window .window-menu,
    .layout-window-controls.is-tts-shell-window .window-menu {
        /* Issue #3752: TTSShellFrame reduces the title inset to 14px at this same shell width. */
        right: 14px;
    }
}

.window-caption-controls__buttons {
    display: flex;
    align-items: center;
}

.window-menu-dropdown {
    /* Issue #3894: Theme the neutral DOM surface; inverse foregrounds and state fills keep their own roles. */
    position: absolute;
    /* Anchor below the 28px caption controls without covering their focus ring. */
    top: 38px;
    right: 20px;
    min-width: 190px;
    max-height: min( 26rem, calc( 100vh - 4rem ) );
    overflow: auto;
    pointer-events: auto;

    display: flex;
    flex-direction: column;
    gap: 2px;

    padding: 6px;
    background: var(--tt-color-surface);
    border: 1px solid var(--tt-color-border-soft);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgb(from var(--tt-color-black) r g b / 0.12);
    z-index: 6;
}

.layout-window.is-modal-window .window-menu-dropdown {
    top: 38px;
    right: 20px;
    z-index: calc( 85 + var(--window-z, 1) );
}

.layout-window.is-modal-window.is-wizard-window .window-menu-dropdown,
.layout-window-controls.is-wizard-window .window-menu-dropdown {
    /* The menu follows the wizard header edge regardless of modal or grid placement. */
    top: 56px;
    right: 8px;
}

.window-menu-item {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.25rem 0.4rem;
    background: transparent;
    border: none;
    border-radius: 3px;
    text-align: left;
    width: 100%;
    cursor: pointer;
    font-size: var(--tt-font-size-normal);
    color: var(--tt-color-heading);
}

.window-menu-item[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
}

.window-menu-item[disabled]:hover {
    background: transparent;
}

.window-menu-item:hover {
    background: var(--tt-color-surface-highlight);
}

.window-menu-item .tticon {
    flex: 0 0 auto;
    font-size: 16px;
    line-height: 1;
}

.window-menu-checkbox {
    /* Issue #3894: Theme the editable/control face independently of panels; inverse foregrounds and state fills keep their own roles. */
    position: relative;
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    border: 1px solid var(--tt-color-border-strong);
    border-radius: 2px;
    background: var(--tt-color-control-bg);
}

.window-menu-checkbox.checked::after {
    content: '';
    position: absolute;
    left: 3px;
    top: 1px;
    width: 5px;
    height: 8px;
    border: solid var(--tt-color-accent);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}

.window-menu-sublist {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-left: 0.9rem;
}

.window-menu-item-sub {
    font-size: 0.85rem;
}

.window-menu-divider {
    height: 1px;
    background: var(--tt-color-surface-muted);
    margin: 4px 0;
}

.window-menu-backdrop {
    position: absolute;
    inset: 0;
    z-index: 0;
    background: transparent;
}
</style>
